<template>
  <q-dialog 
    ref="dialogRef" 
    :model-value="modelValue"
    persistent
    maximized 
    seamless 
    transition-show="slide-up"
    transition-hide="slide-down"
    @update:model-value="val => emit('update:modelValue', val)"
    @hide="onDialogHide"
  >
    <q-card class="enhanced-schema-manager" style="pointer-events: auto;">
      <!-- Action Toolbar -->
      <BrickActionToolbar
        :loading="refreshing"
        :has-changes="false"
        :quote-context="quoteContext"
        show-export
        show-import
        @refresh="refreshData"
        @export="downloadTTL"
        @import="showRawTTL"
        @close="onDialogHide"
      >
        <template #title>BRICK Schema Management</template>
        <template #subtitle>{{ site.name }}</template>
      </BrickActionToolbar>

      <!-- Main Content with Sidebar -->
      <q-card-section class="q-pa-none full-height">
        <div class="row full-height">
          <!-- Main Content Area -->
          <div :class="quoteContext ? 'col-9' : 'col-12'">
            <q-tabs 
          v-model="tab" 
          class="text-grey-7 bg-grey-1" 
          active-color="primary" 
          indicator-color="primary" 
          align="justify"
          dense
        >
          <q-tab name="hierarchy" label="Hierarchy" icon="account_tree" />
          <!-- Equipment tab hidden - not functional, equipment is added via Hierarchy tab -->
          <!-- <q-tab name="equipment" label="Equipment" icon="precision_manufacturing" /> -->
          <q-tab name="electrical" label="Electrical" icon="electrical_services" />
          <q-tab name="export" label="Export/Import" icon="import_export" />
        </q-tabs>

        <q-separator />

        <q-tab-panels
          v-model="tab"
          animated
          keep-alive
          class="full-height bg-white"
          style="height: calc(100vh - 140px);"
        >
          <!-- Hierarchy Tab Panel -->
          <q-tab-panel name="hierarchy" class="q-pa-none full-height">
            <HierarchyTabPanel
              :tree-nodes="treeNodes"
              :expanded-nodes="expandedNodes"
              :selected-node="selectedNode"
              :hierarchy-stats="hierarchyStats"
              :loading-hierarchy="loadingHierarchy"
              :quote-context="quoteContext"
              :lifecycle-filter="lifecycleFilter"
              :is-compound-device="isCompoundDevice"
              :get-compound-device-tooltip="getCompoundDeviceTooltip"
              :is-templated-equipment="isTemplatedEquipment"
              :is-templated-device="isTemplatedDevice"
              :is-templated-point="isTemplatedPoint"
              :has-template-update="hasTemplateUpdate"
              :get-template-tooltip="getTemplateTooltip"
              :get-template-info="getTemplateInfo"
              :is-zone-section="isZoneSection"
              :get-documentation-status-color="getDocumentationStatusColor"
              :get-documentation-status-icon="getDocumentationStatusIcon"
              :get-documentation-status="getDocumentationStatus"
              :should-show-space-badge="shouldShowSpaceBadge"
              :get-space-summary-chip-color="getSpaceSummaryChipColor"
              :get-space-summary-icon="getSpaceSummaryIcon"
              :get-space-summary-text="getSpaceSummaryText"
              :get-space-metadata-badge-color="getSpaceMetadataBadgeColor"
              :get-space-metadata-badge-label="getSpaceMetadataBadgeLabel"
              :get-space-metadata-tooltip="getSpaceMetadataTooltip"
              :get-documentation-button-color="getDocumentationButtonColor"
              :get-documentation-percentage="getDocumentationPercentage"
              :get-documentation-badge-color="getDocumentationBadgeColor"
              :get-documentation-missing="getDocumentationMissing"
              :can-add-points-to-equipment="canAddPointsToEquipment"
              :is-controller="isController"
              :is-ahu="isAHU"
              :can-delete-node="canDeleteNode"
              @update:expanded-nodes="expandedNodes = $event"
              @update:selected-node="selectedNode = $event"
              @add-building="hierarchyManager?.showAddBuildingDialog()"
              @edit-building="(node) => hierarchyManager?.editBuilding(node)"
              @add-level="(node) => hierarchyManager?.showAddLevelDialog(node)"
              @edit-level="(node) => hierarchyManager?.editLevel(node)"
              @add-space="(node) => hierarchyManager?.showAddSpaceDialog(node)"
              @edit-space="(node) => hierarchyManager?.editSpace(node)"
              @add-equipment-to-space="(node) => equipmentManager?.showAddEquipmentDialogForNode(node)"
              @add-switchboard-to-space="(node) => electricalManager?.openAddSwitchboardDialog(node)"
              @add-device-to-zone="(node) => equipmentManager?.showAddDeviceDialogForNode(node)"
              @add-point-to-zone="(node) => equipmentManager?.showAddPointDialogForNode(node)"
              @edit-equipment="(node) => equipmentManager?.editEquipment(node)"
              @show-documentation="showDocumentationDialog"
              @show-template-actions="(node) => templateManager?.showTemplateActions(node)"
              @add-device="(node) => equipmentManager?.showAddDeviceDialogForNode(node)"
              @add-point-to-equipment="(node) => equipmentManager?.showAddPointDialogForNode(node)"
              @add-zone-section="(node) => equipmentManager?.showAddZoneSectionDialogForNode(node)"
              @show-controller-io-dialog="openControllerIoUsageDialog"
              @edit-device="(node) => equipmentManager?.editDevice(node)"
              @add-point-to-device="(node) => equipmentManager?.showAddPointDialogForNode(node)"
              @show-template-restriction="showTemplateRestrictedDialog"
              @edit-switchboard="editSwitchboard"
              @view-switchboard-points="viewSwitchboardPoints"
              @edit-point="(node) => equipmentManager?.editPoint(node)"
              @confirm-delete="confirmDelete"
              @context-menu="({ event, node }) => showContextMenu(event, node)"
              @show-space-summary="showSpaceSummary"
              @mark-as-replacement="handleMarkAsReplacement"
              @switch-to-electrical-tab="tab = 'electrical'"
              @select-electrical-node="selectedElectricalNode = $event"
            />
          </q-tab-panel>

          <!-- Equipment Tab Panel - HIDDEN (not functional, equipment added via Hierarchy tab) -->
          <!--
          <q-tab-panel name="equipment" class="q-pa-none full-height">
            <EquipmentTabPanel
              :equipment-list="equipmentList"
              :equipment-columns="equipmentColumns"
              :loading-equipment="loadingEquipment"
              :expanded-equipment="expandedEquipment"
              :get-point-icon="getPointIcon"
              @show-template-manager="() => templateManager?.showTemplateManager()"
              @add-equipment="addEquipment"
              @toggle-equipment-expansion="toggleEquipmentExpansion"
              @convert-to-template="convertToTemplate"
              @edit-template="editTemplate"
              @edit-equipment="(node) => equipmentManager?.editEquipment(node)"
              @delete-equipment="(node) => equipmentManager?.deleteEquipment(node)"
            />
          </q-tab-panel>
          -->

          <!-- Electrical Tab Panel -->
          <q-tab-panel name="electrical" class="q-pa-none full-height">
            <ElectricalTabPanel
              v-model:electrical-search-query="electricalSearchQuery"
              v-model:electrical-expanded-nodes="electricalExpandedNodes"
              :unallocated-counts="unallocatedCounts"
              :loading-electrical="loadingElectrical"
              :electrical-tree-nodes="electricalTreeNodes"
              :selected-electrical-node="selectedElectricalNode"
              :electrical-data="electricalData"
              :can-add-switchboard="contextMenuComposable.canAddSwitchboard"
              @update:selected-electrical-node="selectedElectricalNode = $event"
              @show-unallocated-list="showUnallocatedList"
              @expand-electrical-to-level="expandElectricalToLevel"
              @load-electrical-hierarchy="loadElectricalHierarchy"
              @open-add-existing-switchboard-dialog="openAddExistingSwitchboardDialog"
              @edit-electrical-node="editElectricalNode"
              @delete-electrical-node="deleteElectricalNode"
              @open-ddc-allocation-dialog="openDdcAllocationDialog"
              @deallocate-ddc-controller="deallocateDdcController"
              @open-power-connection-dialog="openPowerConnectionDialog"
              @disconnect-power="disconnectPower"
              @open-switchboard-classification-dialog="openSwitchboardClassificationDialog"
              @view-controller-io="openControllerIoUsageDialog"
            />
          </q-tab-panel>

          <!-- Export/Import Tab Panel -->
          <q-tab-panel name="export" class="q-pa-none full-height">
            <ExportImportTabPanel
              v-model:ttl-preview="ttlPreview"
              @download-ttl="downloadTTL"
              @show-raw-ttl="showRawTTL"
              @copy-ttl-url="copyTTLURL"
            />
          </q-tab-panel>
        </q-tab-panels>
          </div>

          <!-- BOM Sidebar (only visible in quote context) -->
          <div v-if="quoteContext" class="col-3 bg-grey-2 q-pa-md" style="border-left: 1px solid #e0e0e0; overflow-y: auto; height: calc(100vh - 140px);">
            <QuoteBomSidebar
              :quote-id="quoteContext.quote_id"
              :refresh-trigger="bomRefreshTrigger"
              @device-selected="handleDeviceSelected"
            />
          </div>
        </div>
      </q-card-section>
    </q-card>

    <!-- ===================================== -->
    <!-- MANAGER COMPONENTS (handle dialogs) -->
    <!-- ===================================== -->
    <!-- HierarchyManager: Building/Level/Space dialogs and CRUD -->
    <HierarchyManager
      ref="hierarchyManager"
      :site-id="site?.id"
      @refresh-data="refreshData"
      @building-added="refreshData"
      @building-updated="refreshData"
      @building-deleted="refreshData"
      @level-added="refreshData"
      @level-updated="refreshData"
      @level-deleted="refreshData"
      @space-added="refreshData"
      @space-updated="refreshData"
      @space-deleted="refreshData"
    />

    <!-- EquipmentManager: Equipment/Device/Point/ZoneSection dialogs and CRUD -->
    <EquipmentManager
      ref="equipmentManager"
      :site-id="site?.id"
      :quote-context="quoteContext"
      :local-hierarchy-data="localHierarchyData"
      :equipment-list="equipmentList"
      @refresh-data="refreshData"
      @equipment-added="refreshData"
      @equipment-updated="refreshData"
      @equipment-deleted="refreshData"
      @device-added="refreshData"
      @device-updated="refreshData"
      @device-deleted="refreshData"
      @point-added="refreshData"
      @point-updated="refreshData"
      @point-deleted="refreshData"
      @zone-section-added="refreshData"
      @open-ddc-allocation-dialog="openDdcAllocationDialog"
      @open-power-connection-dialog="openPowerConnectionDialog"
    />

    <!-- ElectricalManager: MSB/MSSB/DDC/Power dialogs and operations -->
    <ElectricalManager
      ref="electricalManager"
      :site-id="site?.id"
      @refresh-data="refreshData"
      @electrical-updated="loadElectricalHierarchy"
      @switchboard-added="loadElectricalHierarchy"
      @switchboard-updated="loadElectricalHierarchy"
      @switchboard-deleted="loadElectricalHierarchy"
    />

    <!-- TemplateManager: Template dialogs and operations -->
    <TemplateManager
      ref="templateManager"
      :site-id="site?.id"
      @refresh-data="refreshData"
      @template-created="loadTemplates"
      @template-updated="loadTemplates"
      @template-deleted="loadTemplates"
    />

    <!-- NOTE: Building/Level/Space dialogs are now in HierarchyManager component -->
    <!-- NOTE: Equipment/Device/Point/ZoneSection dialogs are now in EquipmentManager component -->
    <!-- NOTE: Template dialogs (TemplateManagerDialog, TemplateEditor) are now in TemplateManager component -->

    <!-- NOTE: MSB/MSSB/DDC/Power/Switchboard dialogs are now in ElectricalManager component -->

    <!-- Edit/Delete Context Menu -->
    <q-menu
      v-if="nodeMenuTarget"
      v-model="showNodeMenu"
      :target="nodeMenuTarget"
      context-menu
    >
      <q-list dense style="min-width: 150px">
        <template v-for="(item, index) in contextMenuItems" :key="index">
          <q-separator v-if="item.separator" />
          <q-item 
            v-else
            v-close-popup 
            clickable 
            @click="item.action"
          >
            <q-item-section avatar>
              <q-icon :name="item.icon" :color="item.color" />
            </q-item-section>
            <q-item-section>{{ item.label }}</q-item-section>
          </q-item>
        </template>
      </q-list>
    </q-menu>
  </q-dialog>

  <!-- NOTE: Edit Building/Level/Space dialogs are now in HierarchyManager component -->
  <!-- NOTE: Edit Equipment/Device/Point dialogs and LocationPicker are now in EquipmentManager component -->

  <!-- Category Documentation Dialog (NEW SYSTEM) -->
  <CategoryDocumentationDialog
    v-model="categoryDocumentationDialog"
    :equipment="categoryDocumentationEquipment"
    @saved="onEquipmentDocumented"
  />

  <!-- Documentation Dialog (OLD SYSTEM - Kept for backward compatibility) -->
  <DocumentationDialog
    v-model="documentationDialog"
    :equipment-id="documentationEquipment?.id"
    :equipment-type="documentationEquipment?.type"
    :equipment-name="documentationEquipment?.name"
    :brick-class="documentationEquipment?.brickClass"
    :site-id="site.id"
    @documented="onEquipmentDocumented"
    @navigate-to-airflow="onNavigateToAirflow"
  />

  <!-- Context Menu -->
  <BrickContextMenuManager
    v-model="showContextMenuFlag"
    :context-menu-target="contextMenuTarget"
    :context-menu-node="contextMenuNode"
    :can-delete-node="canDeleteNode"
    :can-add-controller="canAddController"
    :has-template-update="hasTemplateUpdate"
    @add-level="(node) => hierarchyManager?.showAddLevelDialog(node)"
    @edit-building="(node) => hierarchyManager?.editBuilding(node)"
    @delete-building="(node) => hierarchyManager?.deleteBuilding(node)"
    @add-space="(node) => hierarchyManager?.showAddSpaceDialog(node)"
    @edit-level="(node) => hierarchyManager?.editLevel(node)"
    @delete-level="(node) => hierarchyManager?.deleteLevel(node)"
    @add-equipment-to-space="(node) => equipmentManager?.showAddEquipmentDialogForNode(node)"
    @add-compound-template="(node) => templateManager?.showCompoundTemplateDialogForSpace(node)"
    @add-switchboard="(node) => electricalManager?.openAddSwitchboardDialog(node)"
    @edit-space="(node) => hierarchyManager?.editSpace(node)"
    @delete-space="(node) => hierarchyManager?.deleteSpace(node)"
    @delete-equipment="(node) => equipmentManager?.deleteEquipment(node)"
    @edit-point="(node) => equipmentManager?.editPoint(node)"
    @delete-point="(node) => equipmentManager?.deletePoint(node)"
    @add-device="(node) => equipmentManager?.showAddDeviceDialogForNode(node)"
    @add-point="(node) => equipmentManager?.showAddPointDialogForNode(node)"
    @save-as-template="(node) => templateManager?.saveAsTemplate(node)"
    @duplicate-zone="duplicateZone"
    @confirm-delete="confirmDelete"
    @view-controller-io="openControllerIoUsageDialog"
    @edit-equipment="(node) => equipmentManager?.editEquipment(node)"
    @add-zone-section="(node) => equipmentManager?.showAddZoneSectionDialogForNode(node)"
    @sync-template="(node) => templateManager?.syncWithTemplate(node)"
    @detach-template="(node) => templateManager?.detachFromTemplate(node)"
    @rename-zone-section="renameZoneSection"
    @create-zone-template="createZoneTemplate"
    @assign-controller="openAssignControllerDialog"
    @add-controller="openAddControllerDialog"
    @unassign-controller="unassignFromController"
    @assemble-compound="startDeviceCompoundAssembly"
    @disassemble-compound="showDisassembleDeviceConfirm"
    @edit-device="(node) => equipmentManager?.editDevice(node)"
    @change-device-location="changeDeviceLocation"
    @view-node-details="viewNodeDetails"
    @refresh-node="refreshNode"
  />

  <!-- NOTE: SaveAsTemplateDialog is now in TemplateManager component -->

  <!-- Compound Equipment Assembly Dialog -->
  <CompoundAssemblyDialog
    v-model="compoundOps.compoundAssemblyDialogVisible.value"
    :form-data="compoundOps.compoundFormData.value"
    :selected-items="compoundOps.selectedComponentsForAssembly.value"
    :brick-class-options="compoundBrickClasses"
    :parent-role-options="parentRoleOptions"
    :relationship-options="relationshipOptions"
    :saving="compoundOps.assemblingCompound.value"
    @update:name="compoundOps.compoundFormData.value.name = $event"
    @update:description="compoundOps.compoundFormData.value.description = $event"
    @update:brick-class="compoundOps.compoundFormData.value.brick_class = $event"
    @update:parent-role="compoundOps.compoundFormData.value.parent_role = $event"
    @update:relationship="(itemId, relationship) => {
      const item = compoundOps.selectedComponentsForAssembly.value.find(i => i.id === itemId)
      if (item) item.relationship = relationship
    }"
    @submit="submitCompoundAssembly"
    @cancel="compoundOps.closeCompoundAssemblyDialog"
  />

  <!-- Compound Disassembly Dialog -->
  <CompoundDisassemblyDialog
    v-model="showDisassembleDialog"
    :item="compoundToDisassemble"
    :components="compoundComponents"
    :loading="savingDisassemble"
    @confirm="compoundToDisassemble?.type === 'device' ? confirmDisassembleDevice() : confirmDisassemble()"
  />

  <!-- Compound Template Dialog -->
  <CompoundTemplateDialog
    v-model="showCompoundTemplateDialog"
    :templates="compoundTemplates"
    :selected-template="selectedCompoundTemplate"
    :template-data="selectedCompoundTemplate ? compoundTemplates.find(t => t.id === selectedCompoundTemplate) : null"
    :form-data="compoundOps.compoundFormData.value"
    :loading-templates="loadingTemplates"
    :saving="compoundOps.assemblingCompound.value"
    @update:selected-template="selectedCompoundTemplate = $event"
    @update:name="compoundOps.compoundFormData.value.name = $event"
    @update:description="compoundOps.compoundFormData.value.description = $event"
    @submit="createFromTemplateWrapper"
  />

  <!-- Template Usage Locations Dialog -->
  <TemplateUsageDialog
    v-model="showUsageLocationsDialog"
    :template-name="usageTemplate?.name || ''"
    :locations="usageLocations"
    :columns="usageLocationColumns"
    :deleting-item="deletingUsageItem"
    :deleting-all="deletingAllUsages"
    :deleting-template="deletingTemplate"
    @delete-item="deleteUsageItem"
    @delete-all="confirmDeleteAllUsages"
    @retry-delete-template="retryDeleteTemplate"
    @close="closeUsageDialog"
  />

  <!-- Controller Assignment Dialog -->
  <AssignEquipmentToControllerDialog
    v-model="showAssignControllerDialog"
    :equipment="selectedEquipmentForController"
    :site-id="siteId"
    @assigned="handleControllerAssigned"
  />

  <!-- Add Controller Dialog -->
  <AddControllerDialog
    v-model="showAddControllerDialog"
    :parent-equipment="selectedParentForController"
    :site-id="siteId"
    @controller-added="handleControllerAdded"
  />

  <!-- Controller I/O Usage Panel -->
  <ControllerIoUsagePanel
    v-model="showControllerIoUsageDialog"
    :controller="selectedControllerForUsage"
    :site-id="siteId"
    @edit-device="handleEditDeviceFromIo"
  />
</template>

<script setup>
  import { ref, computed, onMounted, watch, nextTick } from 'vue';
  import { useQuasar } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';
  import { api } from '@/services/api';
  import { quoteService } from '@/services/quoteService';
  import { useBrickClassOptions, useTemplateProtection, useSpaceAnalytics } from '@/composables/brick-schema';
  import { useBuildingApi, useEquipmentApi, useDeviceApi, usePointApi, useTemplateApi, useElectricalApi } from '@/composables/brick-schema/api';
  import { useTemplateManagement, useSwitchboardManagement, usePowerDistribution, useDdcAllocation, useCompoundOperations, useTreeNavigation, useExportImport, useNodeRendering, useContextMenu, useFormHelpers, useNodeTypeHelpers } from '@/composables/brick-schema/logic';
  import { useDialogState } from '@/composables/brick-schema/useDialogState';
  import { useEquipmentAssignment } from '@/composables/equipment/useEquipmentAssignment';
  import { useTreeBuilder, useSankeyRenderer, useElectricalLayout, useAirflowVisualization } from '@/composables/brick-schema/visualization';
  import { useDocumentationDialog } from '@/composables/brick-schema/ui/useDocumentationDialog';
  import {
    useHierarchyOperations,
    useBrickValidation,
    useZoneSectionOperations,
  } from '@/composables/brick-schema/operations';
  import { useSpaceSummary } from '@/composables/brick-schema/ui/useSpaceSummary';
  // NOTE: TemplateEditor is now in TemplateManager component
  import DocumentationDialog from './DocumentationDialog.vue';
  import CategoryDocumentationDialog from './CategoryDocumentationDialog.vue';
  // NOTE: PointUnitSelector used by equipment manager
  // NOTE: Dialog imports removed - now handled by manager components:
  // - HierarchyManager: AddBuildingDialog, AddLevelDialog, AddSpaceDialog, EditBuildingDialog, EditLevelDialog, EditSpaceDialog
  // - EquipmentManager: EquipmentFormDialog, DeviceFormDialog, PointFormDialog, EditEquipmentDialog, EditDeviceDialog, EditPointDialog, AddZoneSectionDialog, LocationPickerDialog
  // - ElectricalManager: AddMsbDialog, AddMssbDialog, AddDdcDialog, DdcAllocationDialog, PowerConnectionDialog, SwitchboardClassificationDialog, UnallocatedSwitchboardsDialog, AddSwitchboardDialog, AddExistingSwitchboardDialog
  // - TemplateManager: TemplateEditor, TemplateManagerDialog, SaveAsTemplateDialog, CompoundTemplateDialog, TemplateUsageDialog
  import CompoundAssemblyDialog from './dialogs/CompoundAssemblyDialog.vue';
  import CompoundDisassemblyDialog from './dialogs/CompoundDisassemblyDialog.vue';
  import BrickContextMenuManager from '../brick/BrickContextMenuManager.vue';
  import HierarchyTabPanel from './tabs/HierarchyTabPanel.vue';
  import EquipmentTabPanel from './tabs/EquipmentTabPanel.vue';
  import ElectricalTabPanel from './tabs/ElectricalTabPanel.vue';
  import ExportImportTabPanel from './tabs/ExportImportTabPanel.vue';
  import AssignEquipmentToControllerDialog from '../equipment/AssignEquipmentToControllerDialog.vue';
  import AddControllerDialog from '../equipment/AddControllerDialog.vue';
  import ControllerIoUsagePanel from '../equipment/ControllerIoUsagePanel.vue';
  import BrickActionToolbar from '../brick/BrickActionToolbar.vue';
  import QuoteBomSidebar from '../quotes/QuoteBomSidebar.vue';

  // Import Manager Components (handle hierarchy, equipment, electrical, template operations)
  import HierarchyManager from './managers/HierarchyManager.vue';
  import EquipmentManager from './managers/EquipmentManager.vue';
  import ElectricalManager from './managers/ElectricalManager.vue';
  import TemplateManager from './managers/TemplateManager.vue';

  // Import BRICK class options from composable
  const {
    buildingBrickClasses,
    levelBrickClasses,
    spaceBrickClasses,
    equipmentBrickClasses,
    pointBrickClasses,
    deviceBrickClasses,
    serviceTypeOptions,
    controllerTypeOptions,
    parentRoleOptions,
    relationshipOptions,
    zoneSectionTypeOptions,
    getCompoundBrickClasses,
    templateTypeOptions: templateTypeOptionsFromComposable,
  } = useBrickClassOptions();

  // Import template protection functions from composable
  const {
    isController,
    isTemplatedEquipment,
    isTemplatedDevice,
    isTemplatedPoint,
    canAddPointsToEquipment,
    getTemplateInfo,
  } = useTemplateProtection();

  // Import space analytics functions from composable
  const {
    getSpaceAirflowSupply,
    getSpaceTemperatureSensors,
  } = useSpaceAnalytics();

  // Define component props - Enhanced Design System Style
  const props = defineProps({
    site: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    hierarchyData: {
      type: Object,
      default: () => ({}),
    },
    quoteContext: {
      type: Object,
      default: null,
      // quoteContext shape: { quote_id, quote_number, option_name, is_locked }
    },
  });

  const emit = defineEmits(['update:modelValue', 'save', 'download', 'hide']);

  // Dialog plugin setup (for compatibility with BrickSchemaManager)
  const dialogRef = ref(null);

  // ===================================
  // MANAGER COMPONENT REFS
  // ===================================
  // These manager components handle all dialog operations and expose methods via defineExpose
  const hierarchyManager = ref(null);
  const equipmentManager = ref(null);
  const electricalManager = ref(null);
  const templateManager = ref(null);
  const onDialogHide = () => {
    emit('update:modelValue', false);
    emit('hide');
  };

  const $q = useQuasar();

  // Initialize building/hierarchy API composable
  const siteId = computed(() => props.site?.id);
  const buildingApi = useBuildingApi(siteId);
  const equipmentApi = useEquipmentApi(siteId);
  const deviceApi = useDeviceApi(siteId);
  const pointApi = usePointApi(siteId);
  const templateApi = useTemplateApi(siteId);
  const templateManagement = useTemplateManagement();

  // Initialize electrical system composables
  const electricalApi = useElectricalApi(siteId);
  const switchboardMgmt = useSwitchboardManagement();
  const powerDist = usePowerDistribution();
  const ddcAllocation = useDdcAllocation();

  // Initialize compound operations composable
  const compoundOps = useCompoundOperations();

  // Initialize tree navigation composable
  const treeNav = useTreeNavigation();

  // Initialize context menu composable
  const contextMenuComposable = useContextMenu();

  // Initialize equipment assignment composable
  const { unassignController: unassignControllerApi } = useEquipmentAssignment();

  // Initialize export/import composable
  const exportImport = useExportImport(siteId, computed(() => props.site?.name));

  // Initialize node type helpers composable
  const nodeTypeHelpers = useNodeTypeHelpers();
  const {
    isCompoundEquipment,
    getCompoundTooltip,
    isCompoundDevice,
    getCompoundDeviceTooltip,
    isAHU,
    isZoneSection,
    isVAVOrTerminalUnit,
    isZone,
    getSwitchboardTypeLabel,
  } = nodeTypeHelpers;

  // Initialize hierarchy operations composable
  const hierarchyOps = useHierarchyOperations();

  // Initialize BRICK validation composable
  const brickValidation = useBrickValidation();
  const {
    formatBrickClass,
    toBrickClass,
    extractClassName,
    isEquipmentClass,
    isDeviceClass,
    isPointClass,
    isSpaceClass,
    isAhuClass,
    isControllerClass,
    isElectricalClass,
    inferPointType,
    inferQuantityKind: brickInferQuantityKind,
    inferUnit,
    getEquipmentIcon,
    getEquipmentColor,
  } = brickValidation;

  // Initialize zone section operations composable
  const zoneSectionOps = useZoneSectionOperations();

  // Initialize space summary composable
  const spaceSummary = useSpaceSummary();

  // Initialize unified dialog state composable
  const {
    dialogs,
    openDialog,
    closeDialog,
    closeAllDialogs,
    isOpen,
    getDialogData,
    getDialogMode,
    showLocationPickerDialog,
    showUsageLocationsDialog,
    showLegacyAirflowTableDialog,
    showContextMenuDialog,
    showSaveAsTemplateDialog,
    showMsbDialog,
    showMssbDialog,
    showDdcDialog,
    showDdcAllocationDialog,
    showPowerConnectionDialog,
    showAssignControllerDialog,
    showAddControllerDialog,
    showControllerIoUsageDialog,
    showSwitchboardClassificationDialog,
    showNodeMenuDialog,
    showBuildingDialog,
    showLevelDialog,
    showSpaceDialog,
    showEquipmentDialog,
    showPointDialog,
    showZoneSectionDialog,
    showTemplateDialog,
    showTemplateManagerDialog,
    showTemplateEditorDialog,
    showLocationDialog,
    showDeviceDialog,
    showAddSwitchboardDialog,
    showAddSwitchboardDialogDialog,
    showUnallocatedDialog,
    showEquipmentAssignmentDialog,
    showDdcPointsDialog,
    showDisassembleDialog,
    showCompoundTemplateDialog,
    showEditBuildingDialog,
    showEditLevelDialog,
    showEditSpaceDialog,
    showEditEquipmentDialog,
    showEditDeviceDialog,
    showEditPointDialog,
  } = useDialogState();

  // Create aliases for dialogs that don't have "Dialog" suffix in original code
  const showLocationPicker = showLocationPickerDialog;
  const showLegacyAirflowTable = showLegacyAirflowTableDialog;
  const showContextMenuFlag = showContextMenuDialog;
  const showNodeMenu = showNodeMenuDialog;
  const showTemplateEditor = showTemplateEditorDialog;
  const showAddSwitchboard = showAddSwitchboardDialog;
  const showEquipmentAssignment = showEquipmentAssignmentDialog;
  const showMSBDialog = showMsbDialog;
  const showMSSBDialog = showMssbDialog;
  const showDDCDialog = showDdcDialog;
  const showDDCPointsDialog = showDdcPointsDialog;

  // Initialize documentation dialog composable
  const findAhuNode = (nodeId) => {
    // Recursively search for the node in the tree
    const searchTree = (nodes) => {
      for (const node of nodes) {
        if (node.id === nodeId) {return node;}
        if (node.children) {
          const found = searchTree(node.children);
          if (found) {return found;}
        }
      }
      return null;
    };

    return searchTree(ahuTreeNodes.value);
  };

  const documentationComposable = useDocumentationDialog({
    siteId: siteId.value,
    onNavigate: ({ tab: targetTab, ahuId, ahuName, equipment }) => {
      // Handle navigation to airflow tab
      tab.value = targetTab;
      if (targetTab === 'airflow' && ahuId) {
        selectedAhu.value = ahuId;
        selectedAhuName.value = ahuName;
        // Load airflow data
        nextTick(() => {
          onAhuSelected(selectedAhu.value);
        });
      }
    },
    notifySuccess: (message) => {
      $q.notify({
        type: 'positive',
        message,
      });
    },
    notifyError: (message) => {
      $q.notify({
        type: 'negative',
        message,
      });
    },
    findAhuNode,
  });

  // Destructure documentation dialog state and methods
  const {
    documentationDialog,
    documentationEquipment,
    categoryDocumentationDialog,
    categoryDocumentationEquipment,
    documentationStatuses,
    documentationRefreshTrigger,
    showDocumentationDialog,
    documentAhu,
    documentSelectedAhu,
    onEquipmentDocumented,
    navigateToAirflow: onNavigateToAirflow,
    loadDocumentationStatuses,
  } = documentationComposable;

  // Initialize electrical layout composable
  const {
    electricalLayoutMode,
    electricalGroupCollapsed,
    expandedElectricalNodes,
    isGroupedLayout,
    isMatrixLayout,
    applyMatrixLayout,
    smartStackWithCollapsedDBs,
    groupDBsByType,
    processElectricalDataWithCollapse,
    toggleElectricalNodeExpansion,
    isElectricalNodeExpanded,
    resetExpansionState,
    expandElectricalToLevel,
    setLayoutMode,
    toggleGroupCollapsed,
  } = useElectricalLayout();

  // Initialize airflow visualization composable
  const airflowViz = useAirflowVisualization();
  const {
    loadingAhuTree,
    ahuTreeNodes,
    selectedAhu,
    selectedAhuName,
    spaceAirflowData,
    airflowData,
    loadingAirflow,
    airflowTableData,
    // Note: loadAhuTree, onAhuSelected, loadSpaceAirflowData, processAirflowDataForTable
    // are accessed via wrapper functions below that provide closure variables
    resetAirflowState,
  } = airflowViz;

  // Use composable's localHierarchyData
  const localHierarchyData = buildingApi.localHierarchyData;
  // Initialize with props data
  localHierarchyData.value = { ...props.hierarchyData };

  // Watch for prop changes and sync them
  watch(() => props.hierarchyData, (newData) => {
    localHierarchyData.value = { ...newData };
  }, { deep: true });

  // Watch for dialog visibility changes
  watch(() => props.modelValue, async (newVal) => {
    if (newVal) {
      // Dialog is opening, load data if not already loaded
      if (!localHierarchyData.value || localHierarchyData.value.length === 0) {
        await refreshData();
      }
    }
  });

  // Reactive data - Main state
  const tab = ref('hierarchy');

  const refreshing = ref(false);
  const downloading = exportImport.downloadingTTL; // Use composable's downloading state
  const saving = buildingApi.saving; // Use composable's saving ref
  const ttlPreview = exportImport.ttlPreview; // Use composable's TTL preview state
  const selectedNode = treeNav.selectedHierarchyNode; // Use composable's selected node state
  const loadingEquipment = equipmentApi.loadingEquipment; // Use composable's loadingEquipment ref
  const loadingHierarchy = buildingApi.loadingHierarchy; // Use composable's loadingHierarchy ref

  // Quote Context - Lifecycle filter
  const lifecycleFilter = ref('installed'); // 'installed', 'installed_projects', 'all'

  // BOM sidebar refresh trigger (increment to trigger refresh in quote context)
  const bomRefreshTrigger = ref(0);

  // ===================================
  // DIALOG REFS - NOW MANAGED BY useDialogState COMPOSABLE
  // ===================================
  // All dialog refs are now managed centrally by useDialogState
  // Previously had 38+ individual refs - now consolidated
  // Dialog refs extracted from composable above (lines 641-689)

  // Keep these refs that are associated with dialog data (not visibility state)
  const selectedEquipmentForController = ref(null);
  const selectedParentForController = ref(null);
  const selectedControllerForUsage = ref(null);
  // ===================================
  // END DIALOG REFS
  // ===================================

  // Hierarchy-specific data
  const treeNodes = ref([]);

  // Visualization refs and data
  const electricalContainer = ref(null);
  const loadingElectrical = ref(false);
  const electricalData = ref(null);
  const electricalTreeRef = ref(null);  // Reference to ElectricalHierarchyTree component

  // Initialize node rendering composable with required dependencies
  const nodeRendering = useNodeRendering({
    documentationStatuses,
    documentationRefreshTrigger,
    localHierarchyData,
    spaceAirflowData,
    airflowData,
  });

  // Extract node rendering functions
  const {
    getDocumentationStatus,
    getDocumentationStatusColor,
    getDocumentationStatusIcon,
    getDocumentationPercentage,
    getDocumentationButtonColor,
    getDocumentationBadgeColor,
    getDocumentationMissing,
    getSpaceCompletionPercentage,
    getSpaceSummaryButtonColor,
    getSpaceBadgeColor,
    getSpaceSummaryChipColor,
    getSpaceSummaryIcon,
    getSpaceSummaryText,
    shouldShowSpaceBadge,
    getSpaceMetadataBadgeColor,
    getSpaceMetadataBadgeLabel,
    getSpaceMetadataTooltip,
    getPointIcon,
  } = nodeRendering;

  // Initialize tree builder composable
  const treeBuilder = useTreeBuilder();

  // Initialize visualization renderer composables
  const sankeyRenderer = useSankeyRenderer();

  // Initialize form helpers composable (requires reactive refs to be defined first)
  // Will be initialized after all required refs are available
  let formHelpers = null;

  // Template usage locations dialog
  // showUsageLocationsDialog moved to top dialog refs section
  const usageLocations = ref([]);
  const usageTemplate = ref(null);
  const usageLocationColumns = [
    { name: 'name', label: 'Name', field: 'name', align: 'left', sortable: true },
    { name: 'type', label: 'Type', field: 'type', align: 'center', sortable: true },
    { name: 'location', label: 'Location', field: (row) => row, align: 'left' },
    { name: 'actions', label: 'Actions', field: 'actions', align: 'center' },
  ];

  // States for deleting usage items
  const deletingUsageItem = ref(false);
  const deletingAllUsages = ref(false);
  const deletingTemplate = ref(false);

  // Watch for compound template dialog opening to load templates
  watch(showCompoundTemplateDialog, async (newVal) => {
    if (newVal) {
      await loadCompoundTemplates();
    }
  });

  // showLegacyAirflowTable moved to top dialog refs section

  // Context Menu and Template Management
  // showContextMenuFlag moved to top dialog refs section
  const contextMenuTarget = ref(null);
  const contextMenuNode = ref(null);
  const contextMenuItems = ref([]); // Add this for the electrical context menu items
  // showSaveAsTemplateDialog moved to top dialog refs section
  // Template refs now provided by composables
  const savingTemplate = templateApi.saving;
  const templateForm = ref({
    name: '',
    description: '',
    type: 'equipment', // 'equipment' or 'device'
    includeDevices: true,
    includePoints: true,
    scope: 'site', // 'site' or 'global'
    itemData: null,
    deviceCount: 0,
    pointCount: 0,
  });
  const templateUpdateCache = ref({}); // Cache for template update checks
  const equipmentTemplates = ref([]); // Store equipment templates (local for backward compatibility)
  const zoneTemplates = computed(() =>
    equipmentTemplates.value.filter((t) => t.brick_class === 'AHU_Zone_Section'),
  );
  const selectedZoneTemplate = ref(null);
  const switchboardTypes = ref([]); // Switchboard types from TTL file

  // Layout control for electrical chart
  const electricalSearchQuery = ref(''); // For searching nodes

  // Switchboard dialog states
  // showMSBDialog moved to top dialog refs section
  // showMSSBDialog moved to top dialog refs section
  // showDDCDialog moved to top dialog refs section
  const savingMSB = ref(false);
  const savingMSSB = ref(false);
  const savingDDC = ref(false);

  // Electrical Power Distribution Dialog States
  // showDdcAllocationDialog moved to top dialog refs section
  // showPowerConnectionDialog moved to top dialog refs section
  // showSwitchboardClassificationDialog moved to top dialog refs section
  const savingDdcAllocation = ref(false);
  const savingPowerConnection = ref(false);
  const savingSwitchboardClassification = ref(false);

  // Context menu states
  // showNodeMenu moved to top dialog refs section
  const nodeMenuTarget = ref(null);
  const selectedElectricalNode = ref(null);
  const electricalExpandedNodes = ref([]);

  // Switchboard forms
  const msbForm = ref({
    name: '',
    description: '',
    voltage_rating: '415V',
    current_rating: '',
    manufacturer: '',
    model: '',
    location: null,
  });

  const mssbForm = ref({
    parent_msb: null,
    name: '',
    description: '',
    current_rating: '',
    service_type: null,
    location: null,
  });

  const ddcForm = ref({
    parent_mssb: null,
    name: '',
    description: '',
    number_of_points: 0,
    controller_type: null,
    location: null,
  });

  // Electrical Power Distribution Forms
  const ddcAllocationForm = ref({
    equipment_id: null,
    equipment_name: '',
    ddc_panel_id: null,
    apply_to_devices: true,
  });

  const powerConnectionForm = ref({
    equipment_id: null,
    equipment_name: '',
    power_source_id: null,
    power_source_type: '', // 'MSB', 'MSSB', or 'DB'
  });

  const switchboardClassificationForm = ref({
    equipment_id: null,
    classification: null, // 'essential' or 'non-essential'
  });

  const controllerTemplates = ref([]);
  const availableDdcPanels = ref([]);
  const availablePowerSources = ref([]);

  // DDC Panel Controller Template Management
  const ddcControllerToAdd = ref({
    template_id: null,
    quantity: 1,
    position_label: '',
  });

  // Equipment expansion and template states
  const expandedEquipment = ref([]);

  // Hierarchy tree expansion state - use composable's state
  const expandedNodes = treeNav.expandedHierarchyNodes;

  // Dialog states
  // showBuildingDialog moved to top dialog refs section
  // showLevelDialog moved to top dialog refs section
  // showSpaceDialog moved to top dialog refs section
  // showEquipmentDialog moved to top dialog refs section
  // showPointDialog moved to top dialog refs section
  // showZoneSectionDialog moved to top dialog refs section
  // showTemplateDialog moved to top dialog refs section
  // showTemplateManagerDialog moved to top dialog refs section
  // showTemplateEditor moved to top dialog refs section
  // showLocationDialog moved to top dialog refs section

  // Switchboard dialog states
  // showAddSwitchboard moved to top dialog refs section
  // showAddSwitchboardDialog moved to top dialog refs section
  // showUnallocatedDialog moved to top dialog refs section
  // showEquipmentAssignment moved to top dialog refs section
  // showDDCPointsDialog moved to top dialog refs section
  const editingSwitchboard = ref(false);
  const savingSwitchboard = ref(false);
  const savingEquipmentAssignment = ref(false);
  const parentElectricalNode = ref(null);
  const unallocatedType = ref('');
  const unallocatedFilter = ref('');
  const switchboardTab = ref('existing');
  const selectedExistingSwitchboard = ref(null);
  const newSwitchboard = ref({
    name: '',
    type: '',
    space_id: null,
    brick_class: '',
    properties: {},
  });

  // Template editor state
  const selectedTemplateId = ref(null);
  const selectedTemplateType = ref(null);

  // Template manager states
  const templateFilter = ref('');
  const templateTypeFilter = ref('');

  // NOTE: Building/Level/Space form data moved to HierarchyManager component
  // Equipment form data kept here for Equipment Tab Panel dialog
  const newEquipment = ref({
    name: '',
    brick_class: '',
    description: '',
    equipment_template_id: null,
    selectedTemplate: null,
    space_id: null,
    serves_space: null,
  });
  const newZoneSection = ref({
    name: '',
    zone_type: 'mixing',
    serves_space: null,
    brick_class: 'AHU_Zone_Section',
  });
  const selectedAHU = ref(null);

  // Template data needed for compound operations (not yet in TemplateManager)
  const deviceTemplates = ref([]);
  const compoundTemplates = ref([]);
  // showDeviceDialog moved to top dialog refs section

  // Computed property to combine all template options
  const allTemplateOptions = computed(() => {
    const options = [];

    // Add regular device templates
    if (deviceTemplates.value.length > 0) {
      options.push({
        label: 'Device Templates',
        disable: true,
        type: 'header',
      });
      options.push(...deviceTemplates.value);
    }

    // Add compound templates
    if (compoundTemplates.value.length > 0) {
      if (options.length > 0) {
        options.push({ separator: true });
      }
      options.push({
        label: 'Compound Assemblies',
        disable: true,
        type: 'header',
      });
      options.push(...compoundTemplates.value);
    }

    return options;
  });

  const equipmentAssignment = ref({
    equipment_id: null,
    relationship_type: 'controls',
    connection_description: '',
    circuit_reference: '',
  });

  // Template data - now provided by composable
  const availableTemplates = templateApi.availableTemplates;

  // Compound Equipment States
  // Note: Moved to compoundOps composable:
  // - showCompoundDialog → compoundOps.compoundAssemblyDialogVisible
  // - selectedForCompound → compoundOps.selectedComponentsForAssembly
  // - compoundForm → compoundOps.compoundFormData
  // - savingCompound → compoundOps.assemblingCompound
  const selectedCompoundTemplate = ref(null);
  const compoundToDisassemble = ref(null);
  const loadingTemplates = templateApi.loadingTemplates;
  const savingDisassemble = ref(false);
  const compoundComponents = ref([]);

  // Selected items for operations (kept for location picker and other operations)
  const selectedBuilding = ref(null);
  const selectedLevel = ref(null);
  const selectedSpace = ref(null);
  const selectedEquipment = ref(null);

  // Stub for newDevice (actual new device form is in EquipmentManager)
  const newDeviceStub = ref({});

  // Initialize form helpers composable now that all required refs are defined
  // Note: editingDevice is defined later and used for location picker functionality
  formHelpers = useFormHelpers({
    selectedEquipment,
    localHierarchyData,
    newDevice: newDeviceStub,
    editingDevice: ref({}), // Will be replaced with actual editingDevice after it's defined
    showEditDeviceDialog,
    locationTreeNodes: computed(() => locationTreeNodes.value || []),
    showLocationPicker,
  });

  // Extract form helper state and functions
  const {
    deviceLocationPath,
    selectedLocationNode,
    selectedLocationForPicker,
    locationPickerMode,
    pointTypeOptions,
    filterBrickClasses: formFilterBrickClasses,
    getAllowedPointTypes: formGetAllowedPointTypes,
    getFilteredPointTypes: formGetFilteredPointTypes,
    updateLevelDefaults: formUpdateLevelDefaults,
    updateSpaceDefaults: formUpdateSpaceDefaults,
    inferQuantityKind: formInferQuantityKind,
    buildLocationPath: formBuildLocationPath,
    initializeDeviceLocation: formInitializeDeviceLocation,
    onLocationSelected: formOnLocationSelected,
    openLocationPicker: formOpenLocationPicker,
    clearDeviceLocation: formClearDeviceLocation,
    getSelectedLocationLabel: formGetSelectedLocationLabel,
    findNodeByKey: formFindNodeByKey,
    confirmLocationSelection: formConfirmLocationSelection,
    openLocationPickerForSwitchboard: formOpenLocationPickerForSwitchboard,
    getUnitLabel: formGetUnitLabel,
  } = formHelpers;

  // Create wrapper functions to maintain existing API
  const filterBrickClasses = formFilterBrickClasses;
  const getAllowedPointTypes = formGetAllowedPointTypes;
  const getFilteredPointTypes = formGetFilteredPointTypes;
  // NOTE: updateLevelDefaults and updateSpaceDefaults moved to HierarchyManager component
  const inferQuantityKind = formInferQuantityKind;
  const buildLocationPath = formBuildLocationPath;
  const initializeDeviceLocation = formInitializeDeviceLocation;
  const onLocationSelected = formOnLocationSelected;
  const openLocationPicker = formOpenLocationPicker;
  const clearDeviceLocation = formClearDeviceLocation;
  const getSelectedLocationLabel = formGetSelectedLocationLabel;
  const findNodeByKey = formFindNodeByKey;

  // Override confirmLocationSelection to handle device-move mode
  const confirmLocationSelection = async () => {
    // Handle device-move mode separately
    if (locationPickerMode.value === 'device-move' && deviceToMove.value) {
      if (!selectedLocationNode.value) {
        $q.notify({
          type: 'negative',
          message: 'Please select a location',
        });
        return;
      }

      // Find the selected node details
      const node = formFindNodeByKey(locationTreeNodes.value, selectedLocationNode.value);
      if (!node) {
        $q.notify({
          type: 'negative',
          message: 'Invalid location selected',
        });
        return;
      }

      // Determine the location space ID
      let locationSpaceId = null;
      let locationDescription = '';

      // We only support spaces for location_space_id (not equipment)
      if (node.spaceId || node.type === 'space') {
        locationSpaceId = node.spaceId || node.data?.id;
        locationDescription = `Located at: ${node.label}`;

        // Build the full path if available
        if (node.path && Array.isArray(node.path)) {
          locationDescription = `Located at: ${node.path.join(' > ')}`;
        }
      } else {
        $q.notify({
          type: 'negative',
          message: 'Please select a valid space for the device location',
        });
        return;
      }

      // Prepare the update data - use the new location_space_id field
      const updateData = {
        location_space_id: locationSpaceId,
        location_description: locationDescription,
      };

      // Keep backward compatibility: also store in properties for now
      // This can be removed once all devices are migrated
      if (deviceToMove.value.data.properties) {
        updateData.properties = {
          ...(deviceToMove.value.data.properties || {}),
          physical_location: {
            type: 'space',
            id: locationSpaceId,
            name: node.label || '',
            path: node.path || [],
          },
        };
      }

      try {
        // Update the device's physical location metadata via API
        await deviceApi.updateDevice(deviceToMove.value.data.id, updateData);

        $q.notify({
          type: 'positive',
          message: `Successfully updated physical location of ${deviceToMove.value.label} to ${node.label}`,
        });

        // Refresh the device node to show updated metadata
        if (deviceToMove.value) {
          await refreshNode(deviceToMove.value);
        }

        // Clear the device-to-move reference
        deviceToMove.value = null;

        // Close the dialog
        showLocationPicker.value = false;
      } catch (error) {
        console.error('Error updating device location:', error);
        $q.notify({
          type: 'negative',
          message: `Failed to update device location: ${error.message}`,
        });
      }
    } else {
      // Use the default implementation for other modes
      formConfirmLocationSelection();
    }
  };

  const openLocationPickerForSwitchboard = formOpenLocationPickerForSwitchboard;
  const getUnitLabel = formGetUnitLabel;

  // NOTE: Edit dialog states and editing form data for Building/Level/Space moved to HierarchyManager component
  // Equipment/Device/Point editing still needed for dialogs in parent
  const editingEquipment = ref({ id: null, name: '', description: '', brick_class: '', serves_space: null, controller_id: null, power_source_id: null, power_classification: null, ddcControllers: [] });
  const editingDevice = ref({
    id: null,
    name: '',
    brick_class: '',
    serial_number: '',
    device_part_id: null,
    device_part: null,
    properties: {},
    location_space_id: null,
    location_description: '',
  });
  const editingPoint = ref({ id: null, name: '', brick_class: '', point_type: '', unit_mapping_id: null });

  // Switchboard data
  const switchboards = ref([]);
  const selectedSwitchboard = ref(null);
  const availableEquipment = ref([]);
  const ddcPoints = ref([]);
  const groupedDDCPoints = ref({});
  const ddcPointsSummary = ref({});

  // Equipment data - Use composable's equipmentList
  const equipmentList = equipmentApi.equipmentList;


  // Equipment table columns
  const equipmentColumns = [
    { name: 'name', label: 'Name', field: 'name', align: 'left' },
    { name: 'brickClass', label: 'BRICK Class', field: 'brick_class', align: 'left' },
    { name: 'points', label: 'Points/Devices', field: 'points_count', align: 'center' },
    { name: 'location', label: 'Location', field: 'location', align: 'left' },
    { name: 'actions', label: 'Actions', field: 'actions', align: 'center' },
  ];

  // Template table columns
  const templateColumns = [
    { name: 'name', label: 'Template Name', field: 'name', align: 'left', sortable: true },
    { name: 'type', label: 'Type', field: 'template_type', align: 'left', sortable: true },
    { name: 'brick_class', label: 'BRICK Class', field: 'brick_class', align: 'left', sortable: true },
    { name: 'components', label: 'Components', field: 'component_count', align: 'center', sortable: true },
    { name: 'points', label: 'Points', field: 'total_points', align: 'center', sortable: true },
    { name: 'usage', label: 'Usage Count', field: 'usage_count', align: 'center', sortable: true },
    { name: 'status', label: 'Status', field: 'is_active', align: 'center', sortable: true },
    { name: 'created', label: 'Created', field: 'created_at', align: 'left', sortable: true },
    { name: 'actions', label: 'Actions', field: 'actions', align: 'center' },
  ];

  // BRICK class options now imported from useBrickClassOptions composable

  // Template type filter options (use the composable version)
  const templateTypeOptions = templateTypeOptionsFromComposable;

  // pointBrickClasses, deviceBrickClasses, serviceTypeOptions, and controllerTypeOptions now imported from useBrickClassOptions composable

  // Compound Options - Dynamic based on item type (using composable function)
  const compoundBrickClasses = computed(() => {
    return getCompoundBrickClasses(compoundOps.compoundFormData.value.item_type);
  });

  // parentRoleOptions and relationshipOptions now imported from useBrickClassOptions composable

  const locationOptions = computed(() => {
    const options = [];
    if (localHierarchyData.value?.buildings) {
      localHierarchyData.value.buildings.forEach((building) => {
        options.push({
          label: building.name,
          value: `building_${building.id}`,
        });
        if (building.levels) {
          building.levels.forEach((level) => {
            options.push({
              label: `${building.name} - ${level.name}`,
              value: `level_${level.id}`,
            });
          });
        }
      });
    }
    return options;
  });

  const msbOptions = computed(() => {
    const options = [];
    if (electricalData.value && Array.isArray(electricalData.value)) {
      const findMSBs = (nodes) => {
        nodes.forEach((node) => {
          if (node.type === 'MSB') {
            options.push({
              label: node.name,
              value: node.id,
            });
          }
          if (node.children) {
            findMSBs(node.children);
          }
        });
      };
      findMSBs(electricalData.value);
    }
    return options;
  });

  const mssbOptions = computed(() => {
    const options = [];
    if (electricalData.value && Array.isArray(electricalData.value)) {
      const findMSSBs = (nodes) => {
        nodes.forEach((node) => {
          if (node.type === 'MSSB') {
            options.push({
              label: node.name,
              value: node.id,
            });
          }
          if (node.children) {
            findMSSBs(node.children);
          }
        });
      };
      findMSSBs(electricalData.value);
    }
    return options;
  });

  // Computed properties

  // Location tree for picker dialog
  const locationTreeNodes = computed(() => {
    const nodes = [];
  
    if (!localHierarchyData.value?.buildings) {
      return nodes;
    }
  
    localHierarchyData.value.buildings.forEach((building) => {
      const buildingNode = {
        key: `building-${building.id}`,
        label: building.name,
        icon: 'business',
        selectable: false,
        children: [],
      };
    
      if (building.levels) {
        building.levels.forEach((level) => {
          const levelNode = {
            key: `level-${level.id}`,
            label: level.name,
            icon: 'layers',
            selectable: false,
            children: [],
          };
        
          if (level.spaces) {
            level.spaces.forEach((space) => {
              const spaceNode = {
                key: `space-${space.id}`,
                label: space.name,
                icon: 'meeting_room',
                description: space.description,
                selectable: true,
                spaceId: space.id,
                building: building.name,
                level: level.name,
              };
              levelNode.children.push(spaceNode);
            });
          }
        
          if (levelNode.children.length > 0) {
            buildingNode.children.push(levelNode);
          }
        });
      }
    
      if (buildingNode.children.length > 0) {
        nodes.push(buildingNode);
      }
    });
  
    return nodes;
  });

  const hierarchyStats = computed(() => {
    const stats = { buildings: 0, levels: 0, spaces: 0, equipment: 0, devices: 0, points: 0 };
  
    if (localHierarchyData.value?.buildings) {
      stats.buildings = localHierarchyData.value.buildings.length;
    
      localHierarchyData.value.buildings.forEach((building) => {
        if (building.levels) {
          stats.levels += building.levels.length;
        
          building.levels.forEach((level) => {
            if (level.spaces) {
              stats.spaces += level.spaces.length;
            
              level.spaces.forEach((space) => {
                if (space.equipment) {
                  // Only count equipment without parent_equipment_id at the space level
                  // Equipment with parents will be counted under their parent equipment
                  const rootEquipment = space.equipment.filter((eq) => !eq.parent_equipment_id);
                  stats.equipment += rootEquipment.length;
                
                  // Count all equipment including children, devices and points
                  space.equipment.forEach((equipment) => {
                    // Count zone sections (child equipment) under AHUs
                    if (equipment.zone_sections && Array.isArray(equipment.zone_sections)) {
                      stats.equipment += equipment.zone_sections.length;
                    
                      // Count devices and points under zone sections
                      equipment.zone_sections.forEach((zone) => {
                        if (zone.devices) {
                          stats.devices += zone.devices.length;
                          zone.devices.forEach((device) => {
                            if (device.points) {
                              stats.points += device.points.length;
                            }
                          });
                        }
                        if (zone.points) {
                          stats.points += zone.points.length;
                        }
                      });
                    }
                  
                    // Count devices directly under equipment
                    if (equipment.devices) {
                      stats.devices += equipment.devices.length;
                    
                      // Count points under devices
                      equipment.devices.forEach((device) => {
                        if (device.points) {
                          stats.points += device.points.length;
                        }
                      });
                    }
                  
                    // Count direct points under equipment
                    if (equipment.points) {
                      stats.points += equipment.points.length;
                    }
                  });
                }
              });
            }
          });
        }
      });
    }
  
    return stats;
  });


  // Template computed properties
  const templateFilterTypeOptions = computed(() => {
    const types = new Set();
    availableTemplates.value.forEach((template) => {
      if (template.template_type) {
        types.add(template.template_type);
      }
    });

    return Array.from(types).map((type) => ({
      label: formatTemplateType(type),
      value: type,
    }));
  });

  const filteredTemplates = computed(() => {
    let result = [...availableTemplates.value];

    // Apply text filter
    if (templateFilter.value) {
      const searchLower = templateFilter.value.toLowerCase();
      result = result.filter((template) =>
        template.name.toLowerCase().includes(searchLower) ||
        (template.description && template.description.toLowerCase().includes(searchLower)),
      );
    }

    // Apply type filter
    if (templateTypeFilter.value) {
      result = result.filter((template) => template.template_type === templateTypeFilter.value);
    }

    return result;
  });

  const activeTemplateCount = computed(() => {
    return availableTemplates.value.filter((t) => t.is_active).length;
  });

  const totalTemplateUsage = computed(() => {
    return availableTemplates.value.reduce((sum, t) => sum + (t.usage_count || 0), 0);
  });

  const templateOptions = computed(() => {
    // Combine equipment templates with available templates
    const allTemplates = [...equipmentTemplates.value, ...availableTemplates.value];

    if (!allTemplates.length) {return [];}

    return allTemplates
      .filter((t) => t.is_active && t.template_type === 'equipment')
      .map((template) => ({
        label: template.name,
        value: template.id,
        description: template.description || formatBrickClass(template.brick_class),
        ...template,
      }));
  });

  // Equipment template options for the dropdown
  const equipmentTemplateOptions = computed(() => {
    if (!equipmentTemplates.value.length) {return [];}

    return equipmentTemplates.value.map((template) => ({
      label: template.name,
      value: template.id,
      description: template.description,
      brick_class: template.brick_class,
      point_count: template.point_count || 0,
      ...template,
    }));
  });

  // Computed property for space options in the equipment form
  const spaceOptions = computed(() => {
    const spaces = [];
  
    // Extract all spaces from the hierarchy data
    if (localHierarchyData.value && localHierarchyData.value.buildings) {
      localHierarchyData.value.buildings.forEach((building) => {
        if (building.levels) {
          building.levels.forEach((level) => {
            if (level.spaces) {
              level.spaces.forEach((space) => {
                spaces.push({
                  label: `${building.name} > ${level.name} > ${space.name}`,
                  value: space.id,
                  space_name: space.name,
                  building_name: building.name,
                  level_name: level.name,
                });
              });
            }
          });
        }
      });
    }
  
    return spaces;
  });

  // Removed selectedTemplate computed - using newEquipment.value.selectedTemplate directly

  // Computed property for electrical hierarchy data formatted for the tree component
  const electricalHierarchyData = computed(() => {
    if (!electricalData.value || !Array.isArray(electricalData.value) || electricalData.value.length === 0) {
      // electricalHierarchyData: No data available
      return null;
    }
  
    // Processing electrical hierarchy data
  
    // Convert the electrical data to the format expected by ElectricalHierarchyTree
    const convertNode = (node) => {
      const convertedNode = {
        id: node.id || `node-${Math.random()}`,
        name: node.name || node.label || 'Unnamed',
        type: node.brick_class || node.type || node.equipment_type || '',
        brick_class: node.brick_class || '',
        equipment_type: node.equipment_type || node.type || '',
        equipment_name: node.equipment_name || node.name || '',
        description: node.description || '',
        db_count: node.db_count || 0,
        ddc_count: node.ddc_count || 0,
        children: [],
      };
    
      // Convert children recursively
      if (node.children && Array.isArray(node.children)) {
        convertedNode.children = node.children.map((child) => convertNode(child));
      }
    
      return convertedNode;
    };
  
    // Assume the first item in electricalData is the root (usually the site or main transformer)
    if (electricalData.value.length === 1) {
      const result = convertNode(electricalData.value[0]);
      // Single root electrical node
      return result;
    }
  
    // If multiple root nodes, create a virtual root
    const result = {
      id: 'root',
      name: props.site.name || 'Site',
      type: 'site',
      brick_class: 'brick:Site',
      equipment_type: 'Site',
      children: electricalData.value.map((node) => convertNode(node)),
    };
    // Multiple electrical roots wrapped in virtual root
    return result;
  });

  // Add zoneOptions as an alias for spaceOptions to fix the edit dialog issue
  const zoneOptions = computed(() => spaceOptions.value);

  // zoneSectionTypeOptions now imported from useBrickClassOptions composable

  // Available spaces for zone section to serve
  const availableSpacesForZone = computed(() => {
    const spaces = [];
  
    // Get all spaces from hierarchy
    if (localHierarchyData.value && localHierarchyData.value.buildings) {
      localHierarchyData.value.buildings.forEach((building) => {
        if (building.levels) {
          building.levels.forEach((level) => {
            if (level.spaces) {
              level.spaces.forEach((space) => {
                spaces.push({
                  label: `${building.name} > ${level.name} > ${space.name}`,
                  value: space.id,
                });
              });
            }
          });
        }
      });
    }
  
    return spaces;
  });

  // Computed property for electrical tree nodes formatted for q-tree
  const electricalTreeNodes = computed(() => {
    if (!electricalData.value || !Array.isArray(electricalData.value) || electricalData.value.length === 0) {
      return [];
    }
  
    // Convert electrical data to q-tree format
    const convertToTreeNode = (node) => {
      // Determine icon and color based on brick_class
      let icon = 'device_hub';
      let color = 'grey-6';
    
      const brickClass = node.brick_class || '';
      if (brickClass.includes('Transformer')) {
        icon = 'electrical_services';
        color = 'deep-purple-6';
      } else if (brickClass.includes('Main_Switchboard')) {
        icon = 'dashboard';
        color = 'red-6';
      } else if (brickClass.includes('Mechanical_Services_Switchboard')) {
        icon = 'settings_input_component';
        color = 'orange-6';
      } else if (brickClass.includes('Distribution_Board')) {
        icon = 'view_module';
        color = 'blue-6';
      } else if (brickClass.includes('DDC_Panel')) {
        icon = 'developer_board';
        color = 'yellow-8';
      } else if (brickClass.includes('Switchboard')) {
        icon = 'electrical_services';
        color = 'grey-8';
      } else if (brickClass.includes('Generator')) {
        icon = 'power';
        color = 'orange-8';
      } else if (brickClass.includes('UPS')) {
        icon = 'battery_charging_full';
        color = 'purple-6';
      }
    
      // Build label with voltage/amps/phase for switchboards
      let label = node.name || 'Unnamed';

      // Add electrical specs for switchboards
      if (brickClass.includes('Switchboard')) {
        const props = node.properties
          ? (typeof node.properties === 'string' ? JSON.parse(node.properties) : node.properties)
          : {};

        const parts = [];
        if (props.voltage) {parts.push(`${props.voltage}V`);}
        if (props.rated_capacity || props.amperage) {parts.push(`${props.rated_capacity || props.amperage}A`);}
        if (props.phase_configuration) {
          const phase = props.phase_configuration === 'Three Phase' ? '3ph' : '1ph';
          parts.push(phase);
        }

        if (parts.length > 0) {
          label += ` (${parts.join(', ')})`;
        }
      }

      const treeNode = {
        id: node.id || `node-${Math.random()}`,
        label: label,
        icon: icon,
        color: color,
        brick_class: brickClass,
        type: node.type || node.equipment_type || '',
        db_count: node.db_count || 0,
        ddc_count: node.ddc_count || 0,
        data: node,
        children: [],
      };
    
      // Convert children recursively
      if (node.children && Array.isArray(node.children)) {
        treeNode.children = node.children.map((child) => convertToTreeNode(child));
      }
    
      return treeNode;
    };
  
    // Convert all root nodes
    return electricalData.value.map((node) => convertToTreeNode(node));
  });

  // Computed property for unallocated switchboards
  const unallocatedSwitchboards = computed(() => {
    const allocated = new Set();
  
    // Collect all allocated switchboard IDs from electrical tree
    const collectAllocated = (nodes) => {
      if (!nodes) {return;}
      nodes.forEach((node) => {
        if (node.id) {allocated.add(node.id);}
        if (node.children) {collectAllocated(node.children);}
      });
    };
  
    if (electricalData.value) {
      collectAllocated(electricalData.value);
    }
  
    // Filter equipment list for unallocated switchboards
    return {
      msb: equipmentList.value.filter((eq) => 
        eq.brick_class === 'brick:Main_Switchboard' && !allocated.has(eq.id),
      ),
      mssb: equipmentList.value.filter((eq) => 
        eq.brick_class === 'brick:Mechanical_Services_Switchboard' && !allocated.has(eq.id),
      ),
      db: equipmentList.value.filter((eq) => 
        eq.brick_class === 'brick:Distribution_Board' && !allocated.has(eq.id),
      ),
      ddc: equipmentList.value.filter((eq) => 
        eq.brick_class === 'brick:DDC_Panel' && !allocated.has(eq.id),
      ),
    };
  });

  // Computed property for unallocated counts
  const unallocatedCounts = computed(() => ({
    msb: unallocatedSwitchboards.value.msb.length,
    mssb: unallocatedSwitchboards.value.mssb.length,
    db: unallocatedSwitchboards.value.db.length,
    ddc: unallocatedSwitchboards.value.ddc.length,
  }));

  // Computed property for available switchboards based on selected parent node
  const availableSwitchboardsForParent = computed(() => {
    if (!parentElectricalNode.value) {return [];}

    const parentType = parentElectricalNode.value.type;
    const parentClass = parentElectricalNode.value.brick_class || '';

    // If parent is a SPACE, can add Transformers
    if (parentType === 'space') {
      return unallocatedSwitchboards.value.transformer || [];
    }

    // Apply BRICK hierarchy rules for electrical equipment
    if (parentClass.includes('Transformer')) {
      // Transformers can only have MSBs
      return unallocatedSwitchboards.value.msb;
    } else if (parentClass.includes('Main_Switchboard')) {
      // MSBs can have MSSBs and DBs
      return [
        ...unallocatedSwitchboards.value.mssb,
        ...unallocatedSwitchboards.value.db,
      ];
    } else if (parentClass.includes('Mechanical_Services_Switchboard')) {
      // MSSBs can have DDCs and DBs
      return [
        ...unallocatedSwitchboards.value.ddc,
        ...unallocatedSwitchboards.value.db,
      ];
    }

    return [];
  });

  // Device point type constraints mapping
  // Device point constraints and filtering moved to useFormHelpers composable

  // Helper functions for location breadcrumb
  // Location picker functions moved to useFormHelpers composable
  // Removed availableValueTypes and availableBrickClasses computed properties
  // Now using direct pointBrickClasses and deviceBrickClasses arrays

  // Basic methods
  const getTotalEquipmentCount = () => {
    return hierarchyStats.value.equipment;
  };

  // Documentation methods - moved to useDocumentationDialog composable

  // NOTE: getSpaceAirflowSupply has been moved to useSpaceAnalytics composable

  // NOTE: getSpaceTemperatureSensors has been moved to useSpaceAnalytics composable

  const showSpaceSummary = (node) => {
    if (node.type !== 'space') {return;}
  
    try {
      const equipment = node.children?.filter((child) => child.type === 'equipment') || [];

      // Extract space ID properly
      let spaceId = node.data?.id || node.id;
      if (typeof spaceId === 'string' && spaceId.includes('space-')) {
        spaceId = parseInt(spaceId.replace('space-', ''));
      } else if (typeof spaceId === 'string') {
        spaceId = parseInt(spaceId);
      }
    
    
      // Build the HTML content for the dialog
      let htmlContent = '<div class="q-pa-md">';
    
      // Get airflow supply and temperature sensor information with error handling
      let airflowSupply = null;
      let tempSensors = [];
    
      try {
        airflowSupply = getSpaceAirflowSupply(node, {
          spaceAirflowData: spaceAirflowData.value,
          treeNodes: treeNodes.value,
        });
      } catch (error) {
        console.error('Failed to get space airflow supply:', error);
      }

      try {
        tempSensors = getSpaceTemperatureSensors(node, {
          treeNodes: treeNodes.value,
        });
      } catch (error) {
        console.error('Failed to get space temperature sensors:', error);
      }
  
      // For demonstration, add mock data for Conference Rooms if no real data exists
      let mockAirflowSupply = null;
      if (!airflowSupply && (spaceId === 173 || spaceId === 174)) {
        if (spaceId === 173) {
          mockAirflowSupply = 'AHU-1 → Zone 1, Zone 3 (Demo Data)';
        } else if (spaceId === 174) {
          mockAirflowSupply = 'AHU-1 → Zone 2 (Demo Data)';
        }
      }
  
      const displayAirflowSupply = airflowSupply || mockAirflowSupply;
  
      // Add Space Information section
      htmlContent += '<div class="q-mb-lg">';
      htmlContent += '<strong>📍 Space Information</strong><br/>';
      htmlContent += '<div class="q-mt-sm text-body2">';
      htmlContent += `Name: ${node.label}<br/>`;
      htmlContent += `ID: ${spaceId}<br/>`;
      if (node.data?.building_level_name) {
        htmlContent += `Level: ${node.data.building_level_name}<br/>`;
      }
      htmlContent += '</div>';
      htmlContent += '</div>';
  
      // Add Airflow Supply section
      htmlContent += '<div class="q-mb-lg">';
      if (displayAirflowSupply) {
        htmlContent += '<strong>🌬️ Airflow Supply:</strong><br/>';
        htmlContent += `<div class="q-mt-sm text-body2">${displayAirflowSupply}</div>`;
        if (mockAirflowSupply) {
          htmlContent += '<div class="text-orange q-mt-xs text-caption">Note: This is demonstration data. Configure actual airflow connections in the Airflow tab.</div>';
        }
      } else {
        htmlContent += '<strong>⚠️ Airflow Status:</strong><br/>';
        htmlContent += '<div class="text-warning q-mt-sm">Space not connected to airflow system</div>';
        htmlContent += '<div class="text-caption q-mt-xs">No VAV or terminal unit found. This space may not have proper ventilation.</div>';
      }
      htmlContent += '</div>';
  
      // Add Temperature Monitoring section if sensors exist
      if (tempSensors.length > 0) {
        htmlContent += '<div class="q-mb-lg">';
        htmlContent += '<strong>🌡️ Temperature Monitoring:</strong><br/>';
        htmlContent += '<div class="q-mt-sm text-body2">';
        tempSensors.forEach((sensor) => {
          htmlContent += `• ${sensor.name} (located in ${sensor.location})<br/>`;
        });
        htmlContent += '</div>';
        htmlContent += '</div>';
      }
  
      // Add Equipment section (simplified - table moved to sidebar)
      htmlContent += '<div class="q-mb-md">';
      if (equipment.length > 0) {
        htmlContent += '<strong>🔧 Equipment:</strong><br/>';
        htmlContent += `<div class="text-subtitle2 q-mt-sm">${equipment.length} equipment item(s) in this space</div>`;
        htmlContent += '<div class="text-caption q-mt-xs">View detailed documentation status in the sidebar when this space is selected.</div>';
      } else {
        htmlContent += '<strong>🔧 Equipment:</strong><br/>';
        htmlContent += '<div class="text-subtitle2 q-mt-sm">No equipment in this space</div>';
        htmlContent += '<div class="text-caption q-mt-xs">Add equipment to this space to track documentation.</div>';
      }
      htmlContent += '</div>';

      // Show the dialog with proper content
      $q.dialog({
        title: `Extra Information: ${node.label}`,
        message: htmlContent,
        html: true,
        ok: {
          label: 'Close',
          color: 'primary',
        },
        style: 'min-width: 600px; max-width: 800px;',
      });
    } catch (error) {
      console.error('Failed to load space summary:', error);
      // Show error dialog
      $q.dialog({
        title: 'Error',
        message: 'Failed to load space summary. Please check the console for details.',
        ok: {
          label: 'OK',
          color: 'negative',
        },
      });
    }
  };

  const navigateToAirflow = (node) => {
    // Open documentation dialog on the Airflow tab
    documentationEquipment.value = {
      id: node.data?.id,
      type: 'AHU',
      name: node.label,
      brickClass: node.brickClass || node.data?.brickClass,
    };
    documentationDialog.value = true;
  // The DocumentationDialog will handle switching to the Airflow tab
  };

  const loadAhuTree = async () => {
    // Delegate to composable with closure variables
    await airflowViz.loadAhuTree(equipmentList.value, loadEquipmentList);
  };

  // buildAhuTreeFromHierarchy removed - dead code (never called)
  // loadDocumentationStatuses moved to useDocumentationDialog composable

  // Load airflow data for all spaces - delegate to composable
  const loadSpaceAirflowData = async () => {
    await airflowViz.loadSpaceAirflowData(props.site?.id);
  };

  const onAhuSelected = async (nodeId) => {
    if (!nodeId) {
      selectedAhuName.value = '';
      airflowData.value = null;
      return;
    }
  
    // Find the selected AHU node to get its name and data
    const ahuNode = findAhuNode(nodeId);
    if (!ahuNode) {return;}
  
    selectedAhuName.value = ahuNode.label;
  
    if (!nodeId.startsWith('ahu-')) {return;}
  
    loadingAirflow.value = true;
    airflowData.value = null; // Clear previous data
  
    try {
      // Get the AHU data from the node
      const ahuData = ahuNode.data;
    
      // Calculate statistics from actual equipment
      const vavs = equipmentList.value.filter((eq) => {
        // Check if this is a VAV
        if (!(eq.brick_class === 'brick:Variable_Air_Volume_Box' || 
          eq.brick_class === 'brick:Variable_Air_Volume_Box' ||
          eq.equipment_type === 'VAV')) {
          return false;
        }
      
        // Check if it's connected to this AHU
        // Parse properties field if it exists
        let properties = {};
        try {
          if (eq.properties) {
            properties = typeof eq.properties === 'string' ? JSON.parse(eq.properties) : eq.properties;
          }
        } catch (e) {
          console.warn('Failed to parse properties for equipment:', e);
        }
      
        return properties.feeds_from === ahuData.id || 
          properties.ahu_id === ahuData.id ||
          eq.feeds_from === ahuData.id || 
          eq.ahu_id === ahuData.id ||
          eq.parent_equipment_id === ahuData.id;
      });
    
      // Count spaces served (simplified - you may need to adjust based on your data model)
      const spacesServed = new Set();
      vavs.forEach((vav) => {
        if (vav.location_space_id) {spacesServed.add(vav.location_space_id);}
        if (vav.space_id) {spacesServed.add(vav.space_id);}
      });
    
      // Calculate total airflow (estimated if not available)
      const totalAirflow = ahuData.cfm || ahuData.airflow || 
        vavs.reduce((sum, vav) => sum + (vav.cfm || vav.airflow || 500), 0) ||
        2000; // Default
    
      // Set airflow data for display
      airflowData.value = {
        ahu: ahuData,
        vavs: vavs,
        totalAirflow: totalAirflow,
        spacesServed: spacesServed.size || vavs.length * 3, // Estimate if no direct space data
        vavCount: vavs.length,
      };
    
      // Render Sankey diagram with actual equipment data
      await nextTick();
      await sankeyRenderer.renderSankeyDiagram(ahuData, equipmentList, localHierarchyData);
    
    } catch (error) {
      $q.notify({
        type: 'warning',
        message: 'Unable to load complete airflow data',
      });
    
      // Still try to render with available data
      if (ahuNode.data) {
        airflowData.value = {
          ahu: ahuNode.data,
          vavs: [],
          totalAirflow: 2000,
          spacesServed: 0,
          vavCount: 0,
        };
        await sankeyRenderer.renderSankeyDiagram(ahuNode.data, equipmentList, localHierarchyData);
      }
    } finally {
      loadingAirflow.value = false;
    }
  };

  // renderSankeyDiagram function moved to useSankeyRenderer composable

  // Documentation functions moved to useDocumentationDialog composable:
  // - documentAhu
  // - documentSelectedAhu
  // - findAhuNode
  // - onEquipmentDocumented
  // - onNavigateToAirflow

  // formatBrickClass is now provided by useBrickValidation composable

  // Use composable's tree state preservation functions
  const preserveTreeState = treeNav.preserveHierarchyTreeState;
  const restoreTreeState = treeNav.restoreHierarchyTreeState;
  const preservingTreeState = treeNav.preservingTreeState;

  const refreshDataWithState = async (bypassCache = false, nodesToExpand = []) => {
    const state = preserveTreeState();
    preservingTreeState.value = true;
    await refreshData(bypassCache);
    preservingTreeState.value = false;
    await restoreTreeState(state, nodesToExpand);
  };

  const refreshData = async (bypassCache = false) => {
    refreshing.value = true;
    // Set all loading states immediately
    loadingHierarchy.value = true;
    loadingEquipment.value = true;
    // Refreshing data...
  
    // Ensure spinners render
    await nextTick();
  
    try {
      await loadHierarchyData(bypassCache);
      await loadEquipmentList();
      await loadDocumentationStatuses();
      await loadSpaceAirflowData();  // Refresh airflow connections
      await loadCompoundTemplates();  // Refresh compound templates

      // Trigger BOM sidebar refresh if in quote context
      if (props.quoteContext) {
        bomRefreshTrigger.value++;
      }

      $q.notify({
        type: 'positive',
        message: 'Data refreshed successfully',
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to refresh data: ${error.message}`,
      });
    } finally {
      refreshing.value = false;
    }
  };

  // Electrical Visualization Methods
  const loadElectricalHierarchy = async () => {
    // Set loading state immediately if not already loading
    if (!loadingElectrical.value) {
      loadingElectrical.value = true;
      await nextTick(); // Ensure spinner renders
    }
    try {
      const response = await authenticatedFetch(`/api/sites/${props.site.id}/electrical-hierarchy`, {
        headers: {
          'Accept': 'application/json',
        },
      });
    
      if (!response.ok) {
        electricalData.value = [];
        return;
      }
    
      const data = await response.json();
      electricalData.value = data.data.hierarchy || [];
    
      // The backend now returns grouped data when no relationships exist
    
      // Set default expansion to MSSB level (level 3)
      await nextTick();
      electricalExpandedNodes.value = expandElectricalToLevel(3, electricalTreeNodes.value);
    } catch (error) {
      electricalData.value = [];
    
      $q.notify({
        type: 'negative',
        message: 'Failed to load electrical hierarchy',
      });
    } finally {
      loadingElectrical.value = false;
    }
  };





  // Handle ApexTree node clicks (legacy)
  const handleApexTreeNodeClick = (node) => {
    // ApexTree node clicked
  
    // Find the original node from electricalData
    const findOriginalNode = (nodes, id) => {
      for (const node of nodes) {
        if (node.id === id) {return node;}
        if (node.children) {
          const found = findOriginalNode(node.children, id);
          if (found) {return found;}
        }
      }
      return null;
    };
  
    const originalNode = findOriginalNode(electricalData.value, node.id);
    if (!originalNode) {return;}
  
    // Check if this node has collapsible children
    if (originalNode.brick_class === 'brick:Switchgear' && originalNode.db_count > 0) {
      toggleElectricalNodeExpansion(node.id, 'MSB');
    } else if ((originalNode.brick_class === 'brick:Switchgear' || originalNode.brick_class === 'brick:MSSB') && originalNode.ddc_count > 0) {
      toggleElectricalNodeExpansion(node.id, 'MSSB');
    }
  };

  // Handle ElectricalHierarchyTree node clicks
  const handleElectricalNodeClick = (node) => {
    // Electrical hierarchy node clicked
  
    // Show equipment details or perform action based on node type
    const type = (node.type || node.equipment_type || '').toLowerCase();
  
    if (type.includes('switchboard') || type.includes('msb') || type.includes('mssb')) {
      // Could open edit dialog for switchboards
      editSwitchboard({ 
        id: node.id, 
        name: node.name, 
        type: node.equipment_type,
        description: node.description, 
      });
    } else if (type.includes('ddc')) {
      // Could open DDC panel view
      viewSwitchboardPoints({ 
        id: node.id, 
        name: node.name, 
        type: 'DDC_Panel', 
      });
    } else {
      // Show general equipment info
      $q.notify({
        message: `${node.name} - ${node.equipment_type}`,
        caption: node.description || 'No description available',
        color: 'info',
        icon: 'info',
      });
    }
  };

  // Handle ElectricalHierarchyTree node expansion
  const handleElectricalNodeExpand = ({ nodeId, expanded }) => {
  // Track expanded state if needed for persistence
  };

  // Helper functions for electrical context menu
  const viewElectricalDetails = (node) => {
  // Implement view details functionality
  };

  // Function to show unallocated switchboards list
  const showUnallocatedList = (type) => {
    unallocatedType.value = type;
    unallocatedFilter.value = '';
    showUnallocatedDialog.value = true;
  };

  // Function to open add existing switchboard to electrical tree dialog
  const openAddExistingSwitchboardDialog = (parentNode = null) => {
    parentElectricalNode.value = parentNode;
    selectedExistingSwitchboard.value = null;
    showAddSwitchboard.value = true;
  };

  // Quick add switchboard from unallocated list
  const quickAddToTree = async (switchboard) => {
    if (!selectedElectricalNode.value) {
      $q.notify({
        type: 'warning',
        message: 'Please select a parent node in the tree first',
      });
      return;
    }
  
    try {
      // Create relationship
      const response = await authenticatedFetch('/api/relationships', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source_id: selectedElectricalNode.value.data.id,
          source_type: 'App\\Models\\SiteEquipment',
          target_id: switchboard.id,
          target_type: 'App\\Models\\SiteEquipment',
          relationship_type: 'feeds',
        }),
      });
    
      if (response.ok) {
        // Refresh tree and close dialog
        await loadElectricalHierarchy();
        showUnallocatedDialog.value = false;
      
        $q.notify({
          type: 'positive',
          message: `Added ${switchboard.name} to electrical hierarchy`,
        });
      } else {
        throw new Error('Failed to create relationship');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to add switchboard to tree',
      });
    }
  };

  // Electrical node action functions
  const editElectricalNode = (node) => {
    // Open edit dialog for the electrical node
    if (node.data) {
      selectedEquipment.value = node.data;
      showEditEquipmentDialog.value = true;
    } else {
      $q.notify({
        type: 'info',
        message: 'Edit functionality for this node type is in development',
      });
    }
  };

  const deleteElectricalNode = async (node) => {
    if (!node.data || !node.data.id) {
      $q.notify({
        type: 'warning',
        message: 'Cannot delete this node',
      });
      return;
    }
  
    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete ${node.label}? This will also remove all its connections.`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        const response = await authenticatedFetch(`/api/v2/sites/${props.site.id}/equipment/${node.data.id}`, {
          method: 'DELETE',
        });
      
        if (response.ok) {
          await loadElectricalHierarchy();
          await loadHierarchyData();
          $q.notify({
            type: 'positive',
            message: `${node.label} deleted successfully`,
          });
        } else {
          throw new Error('Failed to delete equipment');
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to delete equipment',
        });
      }
    });
  };

  // ====================================================================================
  // ELECTRICAL POWER DISTRIBUTION FUNCTIONS
  // ====================================================================================

  // Open DDC Allocation Dialog
  const openDdcAllocationDialog = async (node) => {
    try {
      // Load available DDC panels
      const response = await authenticatedFetch(`/api/v2/sites/${props.site.id}/equipment?brick_class=brick:DDC_Panel`);
      if (response.ok) {
        const data = await response.json();
        availableDdcPanels.value = data.data || [];
      }

      // Set form data
      ddcAllocationForm.value = {
        equipment_id: node.data.id,
        equipment_name: node.label,
        ddc_panel_id: node.data.controller?.id || null,
        apply_to_devices: true,
      };

      showDdcAllocationDialog.value = true;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load DDC panels',
      });
    }
  };

  // Save DDC Allocation
  const saveDdcAllocation = async () => {
    if (!ddcAllocationForm.value.ddc_panel_id) {
      $q.notify({
        type: 'warning',
        message: 'Please select a DDC panel',
      });
      return;
    }

    try {
      savingDdcAllocation.value = true;

      const response = await authenticatedFetch(
        `/api/v2/sites/${props.site.id}/equipment/${ddcAllocationForm.value.equipment_id}/allocate-controller`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            controller_id: ddcAllocationForm.value.ddc_panel_id,
            cascade_to_devices: ddcAllocationForm.value.apply_to_devices,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();

        // Refresh electrical tree
        await loadElectricalHierarchy();
        await loadHierarchyData();

        showDdcAllocationDialog.value = false;

        $q.notify({
          type: 'positive',
          message: 'DDC controller allocated successfully',
          caption: data.data.io_usage ?
            `I/O Usage: ${data.data.io_usage.used}/${data.data.io_usage.total} points` :
            undefined,
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to allocate controller');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to allocate DDC controller',
      });
    } finally {
      savingDdcAllocation.value = false;
    }
  };

  // Deallocate DDC Controller
  const deallocateDdcController = async (node) => {
    $q.dialog({
      title: 'Confirm Deallocation',
      message: `Remove DDC controller allocation from ${node.label}?`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        const response = await authenticatedFetch(
          `/api/v2/sites/${props.site.id}/equipment/${node.data.id}/deallocate-controller`,
          {
            method: 'DELETE',
          },
        );

        if (response.ok) {
          await loadElectricalHierarchy();
          await loadHierarchyData();

          $q.notify({
            type: 'positive',
            message: 'DDC controller deallocated successfully',
          });
        } else {
          throw new Error('Failed to deallocate controller');
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to deallocate DDC controller',
        });
      }
    });
  };

  // Open Power Connection Dialog
  const openPowerConnectionDialog = async (node) => {
    try {
      // Load available power sources based on equipment type
      const brickClass = node.brick_class || '';
      let validSources = [];

      // DDC Panels can only be fed by MSSB or DB
      if (brickClass.includes('DDC_Panel')) {
        const mssbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.site.id}/equipment?brick_class=brick:Mechanical_Services_Switchboard`,
        );
        const dbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.site.id}/equipment?brick_class=brick:Distribution_Board`,
        );

        if (mssbResponse.ok && dbResponse.ok) {
          const mssbData = await mssbResponse.json();
          const dbData = await dbResponse.json();
          validSources = [
            ...(mssbData.data || []).map((eq) => ({ ...eq, source_type: 'MSSB' })),
            ...(dbData.data || []).map((eq) => ({ ...eq, source_type: 'DB' })),
          ];
        }
      }
      // MSSB can be fed by MSB or another MSSB
      else if (brickClass.includes('Mechanical_Services_Switchboard')) {
        const msbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.site.id}/equipment?brick_class=brick:Main_Switchboard`,
        );
        const mssbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.site.id}/equipment?brick_class=brick:Mechanical_Services_Switchboard`,
        );

        if (msbResponse.ok && mssbResponse.ok) {
          const msbData = await msbResponse.json();
          const mssbData = await mssbResponse.json();
          validSources = [
            ...(msbData.data || []).map((eq) => ({ ...eq, source_type: 'MSB' })),
            ...(mssbData.data || []).filter((eq) => eq.id !== node.data.id).map((eq) => ({ ...eq, source_type: 'MSSB' })),
          ];
        }
      }
      // Other equipment can be fed by MSB, MSSB, or DB
      else {
        const msbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.site.id}/equipment?brick_class=brick:Main_Switchboard`,
        );
        const mssbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.site.id}/equipment?brick_class=brick:Mechanical_Services_Switchboard`,
        );
        const dbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.site.id}/equipment?brick_class=brick:Distribution_Board`,
        );

        if (msbResponse.ok && mssbResponse.ok && dbResponse.ok) {
          const msbData = await msbResponse.json();
          const mssbData = await mssbResponse.json();
          const dbData = await dbResponse.json();
          validSources = [
            ...(msbData.data || []).map((eq) => ({ ...eq, source_type: 'MSB' })),
            ...(mssbData.data || []).map((eq) => ({ ...eq, source_type: 'MSSB' })),
            ...(dbData.data || []).map((eq) => ({ ...eq, source_type: 'DB' })),
          ];
        }
      }

      availablePowerSources.value = validSources;

      // Set form data
      powerConnectionForm.value = {
        equipment_id: node.data.id,
        equipment_name: node.label,
        power_source_id: node.data.power_source?.id || null,
        power_source_type: '',
      };

      showPowerConnectionDialog.value = true;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load power sources',
      });
    }
  };

  // Save Power Connection
  const savePowerConnection = async () => {
    try {
      savingPowerConnection.value = true;

      // Use power distribution composable to save connection
      await powerDist.savePowerConnection(async (connectionData) => {
        return await electricalApi.savePowerConnection({
          equipment_id: powerConnectionForm.value.equipment_id,
          power_source_id: powerConnectionForm.value.power_source_id,
        });
      });

      await loadElectricalHierarchy();
      await loadHierarchyData();
      showPowerConnectionDialog.value = false;
    } catch (error) {
      console.error('Failed to save power connection:', error);
    } finally {
      savingPowerConnection.value = false;
    }
  };

  // Disconnect Power
  const disconnectPower = async (node) => {
    $q.dialog({
      title: 'Confirm Disconnection',
      message: `Disconnect power source from ${node.label}?`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        const response = await authenticatedFetch(
          `/api/v2/sites/${props.site.id}/equipment/${node.data.id}/disconnect-power`,
          {
            method: 'DELETE',
          },
        );

        if (response.ok) {
          await loadElectricalHierarchy();
          await loadHierarchyData();

          $q.notify({
            type: 'positive',
            message: 'Power disconnected successfully',
          });
        } else {
          throw new Error('Failed to disconnect power');
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to disconnect power',
        });
      }
    });
  };

  // Open Switchboard Classification Dialog
  const openSwitchboardClassificationDialog = (node) => {
    switchboardClassificationForm.value = {
      equipment_id: node.data.id,
      classification: node.data.metadata?.power_classification || null,
    };

    showSwitchboardClassificationDialog.value = true;
  };

  // Save Switchboard Classification
  const saveSwitchboardClassification = async () => {
    try {
      savingSwitchboardClassification.value = true;

      // Use electrical API composable to save classification
      await electricalApi.saveSwitchboardClassification(
        switchboardClassificationForm.value.equipment_id,
        {
          classification: switchboardClassificationForm.value.classification,
        },
      );

      await loadElectricalHierarchy();
      await loadHierarchyData();
      showSwitchboardClassificationDialog.value = false;
    } catch (error) {
      console.error('Failed to save switchboard classification:', error);
    } finally {
      savingSwitchboardClassification.value = false;
    }
  };

  // Get available power sources for equipment based on BRICK rules
  const getAvailablePowerSourcesForEquipment = (equipment) => {
    if (!equipment || !equipment.brick_class) {return [];}

    const allSources = [];
    const brickClass = equipment.brick_class;

    // Load MSB, MSSB, and DB from equipment list
    const msbEquipment = equipmentList.value.filter((eq) =>
      eq.brick_class && eq.brick_class.includes('Main_Switchboard'),
    );
    const mssbEquipment = equipmentList.value.filter((eq) =>
      eq.brick_class && eq.brick_class.includes('Mechanical_Services_Switchboard'),
    );
    const dbEquipment = equipmentList.value.filter((eq) =>
      eq.brick_class && eq.brick_class.includes('Distribution_Board'),
    );

    // DDC Panels can only be fed by MSSB or DB
    if (brickClass.includes('DDC_Panel')) {
      allSources.push(
        ...mssbEquipment.map((eq) => ({
          label: eq.name,
          value: eq.id,
          type: 'Mechanical Services Switchboard',
          badge: 'MSSB',
          color: 'orange',
        })),
        ...dbEquipment.map((eq) => ({
          label: eq.name,
          value: eq.id,
          type: 'Distribution Board',
          badge: 'DB',
          color: 'blue',
        })),
      );
    }
    // MSSB can be fed by MSB or another MSSB
    else if (brickClass.includes('Mechanical_Services_Switchboard')) {
      allSources.push(
        ...msbEquipment.map((eq) => ({
          label: eq.name,
          value: eq.id,
          type: 'Main Switchboard',
          badge: 'MSB',
          color: 'red',
        })),
        ...mssbEquipment.filter((eq) => eq.id !== equipment.id).map((eq) => ({
          label: eq.name,
          value: eq.id,
          type: 'Mechanical Services Switchboard',
          badge: 'MSSB',
          color: 'orange',
        })),
      );
    }
    // Other equipment can be fed by MSB, MSSB, or DB
    else {
      allSources.push(
        ...msbEquipment.map((eq) => ({
          label: eq.name,
          value: eq.id,
          type: 'Main Switchboard',
          badge: 'MSB',
          color: 'red',
        })),
        ...mssbEquipment.map((eq) => ({
          label: eq.name,
          value: eq.id,
          type: 'Mechanical Services Switchboard',
          badge: 'MSSB',
          color: 'orange',
        })),
        ...dbEquipment.map((eq) => ({
          label: eq.name,
          value: eq.id,
          type: 'Distribution Board',
          badge: 'DB',
          color: 'blue',
        })),
      );
    }

    return allSources;
  };

  // Open DDC Allocation Dialog from Equipment Editor
  const openDdcAllocationDialogFromEdit = () => {
    // Close equipment dialog and open DDC allocation dialog
    const equipmentId = editingEquipment.value.id;
    const equipmentName = editingEquipment.value.name;

    showEditEquipmentDialog.value = false;

    // Find the equipment node
    const findEquipmentNode = (nodes) => {
      for (const node of nodes) {
        if (node.data && node.data.id === equipmentId) {
          return node;
        }
        if (node.children) {
          const found = findEquipmentNode(node.children);
          if (found) {return found;}
        }
      }
      return null;
    };

    const equipmentNode = findEquipmentNode(localHierarchyData.value?.children || []);
    if (equipmentNode) {
      openDdcAllocationDialog(equipmentNode);
    } else {
      // Fallback: create a temporary node structure
      openDdcAllocationDialog({
        data: {
          id: equipmentId,
          name: equipmentName,
        },
        label: equipmentName,
      });
    }
  };

  // Open Power Connection Dialog from Equipment Editor
  const openPowerConnectionDialogFromEdit = () => {
    // Close equipment dialog and open power connection dialog
    const equipmentId = editingEquipment.value.id;
    const equipmentName = editingEquipment.value.name;
    const brickClass = editingEquipment.value.brick_class;

    showEditEquipmentDialog.value = false;

    // Create a temporary node structure
    openPowerConnectionDialog({
      data: {
        id: equipmentId,
        name: equipmentName,
        brick_class: brickClass,
      },
      label: equipmentName,
      brick_class: brickClass,
    });
  };

  // ====================================================================================
  // DDC PANEL CONTROLLER TEMPLATE MANAGEMENT
  // ====================================================================================

  // Load controller templates from API
  const loadControllerTemplatesForDdc = async () => {
    try {
      const response = await authenticatedFetch('/api/v2/controller-templates');
      if (response.ok) {
        const data = await response.json();
        controllerTemplates.value = data.data || [];
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load controller templates',
      });
    }
  };

  // Add controller template to DDC panel
  const addControllerToDdc = () => {
    if (!ddcControllerToAdd.value.template_id) {
      $q.notify({
        type: 'warning',
        message: 'Please select a controller template',
      });
      return;
    }

    const template = controllerTemplates.value.find((t) => t.id === ddcControllerToAdd.value.template_id);
    if (!template) {
      $q.notify({
        type: 'warning',
        message: 'Template not found',
      });
      return;
    }

    // Add to the list with pivot data
    const controllerWithPivot = {
      ...template,
      pivot: {
        quantity: ddcControllerToAdd.value.quantity || 1,
        position_label: ddcControllerToAdd.value.position_label || null,
      },
    };

    if (!editingEquipment.value.ddcControllers) {
      editingEquipment.value.ddcControllers = [];
    }

    editingEquipment.value.ddcControllers.push(controllerWithPivot);

    // Reset form
    ddcControllerToAdd.value = {
      template_id: null,
      quantity: 1,
      position_label: '',
    };

    $q.notify({
      type: 'positive',
      message: 'Controller added to panel',
    });
  };

  // Remove controller from DDC panel
  const removeControllerFromDdc = (index) => {
    editingEquipment.value.ddcControllers.splice(index, 1);

    $q.notify({
      type: 'info',
      message: 'Controller removed from panel',
    });
  };

  // Calculate total I/O capacity for DDC panel
  const calculateTotalIoForDdc = () => {
    if (!editingEquipment.value.ddcControllers || editingEquipment.value.ddcControllers.length === 0) {
      return 0;
    }

    return editingEquipment.value.ddcControllers.reduce((total, ctrl) => {
      const ioPoints = ctrl.total_io_points || 0;
      const quantity = ctrl.pivot?.quantity || 1;
      return total + (ioPoints * quantity);
    }, 0);
  };

  // Check if switchboard can be quickly added
  const canQuickAdd = (switchboard) => {
    if (!selectedElectricalNode.value) {return false;}
  
    const parentClass = selectedElectricalNode.value.brick_class || '';
    const switchboardClass = switchboard.brick_class || '';
  
    // Apply BRICK hierarchy rules
    if (parentClass.includes('Transformer')) {
      return switchboardClass === 'brick:Main_Switchboard';
    } else if (parentClass.includes('Main_Switchboard')) {
      return switchboardClass === 'brick:Mechanical_Services_Switchboard' ||
        switchboardClass === 'brick:Distribution_Board';
    } else if (parentClass.includes('Mechanical_Services_Switchboard')) {
      return switchboardClass === 'brick:DDC_Panel' ||
        switchboardClass === 'brick:Distribution_Board';
    }
  
    return false;
  };

  // Get switchboard type label
  const getSwitchboardType = (equipment) => {
    const brickClass = equipment.brick_class || '';
    if (brickClass.includes('Main_Switchboard')) {return 'MSB';}
    if (brickClass.includes('Mechanical_Services_Switchboard')) {return 'MSSB';}
    if (brickClass.includes('Distribution_Board')) {return 'DB';}
    if (brickClass.includes('DDC_Panel')) {return 'DDC';}
    return 'Unknown';
  };

  // Get switchboard icon
  const getSwitchboardIcon = (equipment) => {
    const brickClass = equipment.brick_class || '';
    if (brickClass.includes('Main_Switchboard')) {return 'dashboard';}
    if (brickClass.includes('Mechanical_Services_Switchboard')) {return 'settings_input_component';}
    if (brickClass.includes('Distribution_Board')) {return 'view_module';}
    if (brickClass.includes('DDC_Panel')) {return 'developer_board';}
    return 'electrical_services';
  };

  // Get switchboard color
  const getSwitchboardColor = (equipment) => {
    const brickClass = equipment.brick_class || '';
    if (brickClass.includes('Main_Switchboard')) {return 'red';}
    if (brickClass.includes('Mechanical_Services_Switchboard')) {return 'orange';}
    if (brickClass.includes('Distribution_Board')) {return 'blue';}
    if (brickClass.includes('DDC_Panel')) {return 'yellow';}
    return 'grey';
  };

  // Computed property for filtered unallocated list
  const filteredUnallocatedList = computed(() => {
    let list = [];
    if (unallocatedType.value === 'MSB') {list = unallocatedSwitchboards.value.msb;}
    else if (unallocatedType.value === 'MSSB') {list = unallocatedSwitchboards.value.mssb;}
    else if (unallocatedType.value === 'DB') {list = unallocatedSwitchboards.value.db;}
    else if (unallocatedType.value === 'DDC') {list = unallocatedSwitchboards.value.ddc;}
  
    if (!unallocatedFilter.value) {return list;}
  
    const filter = unallocatedFilter.value.toLowerCase();
    return list.filter((item) => 
      item.name.toLowerCase().includes(filter) ||
      (item.description && item.description.toLowerCase().includes(filter)),
    );
  });

  // Get available new switchboard types based on parent
  const getAvailableNewSwitchboardTypes = () => {
    if (!parentElectricalNode.value) {return [];}

    const parentType = parentElectricalNode.value.type;
    const parentClass = parentElectricalNode.value.brick_class || '';
    const options = [];

    // If parent is a SPACE (not electrical equipment), allow creating a Transformer (root of electrical hierarchy)
    if (parentType === 'space') {
      options.push({ label: 'Transformer', value: 'Transformer' });
      return options;
    }

    // Handle electrical equipment hierarchy
    if (parentClass.includes('Transformer')) {
      options.push({ label: 'Main Switchboard (MSB)', value: 'MSB' });
    } else if (parentClass.includes('Main_Switchboard')) {
      options.push(
        { label: 'Mechanical Services Switchboard (MSSB)', value: 'MSSB' },
        { label: 'Distribution Board (DB)', value: 'DB' },
      );
    } else if (parentClass.includes('Mechanical_Services_Switchboard')) {
      options.push(
        { label: 'DDC Panel', value: 'DDC' },
        { label: 'Distribution Board (DB)', value: 'DB' },
      );
    }

    return options;
  };

  // Get message for no available switchboards
  const getNoSwitchboardsMessage = () => {
    const parentType = parentElectricalNode.value?.type;
    const parentClass = parentElectricalNode.value?.brick_class || '';

    if (parentType === 'space') {
      return 'Spaces can have Transformers added. All Transformers are already allocated or none exist.';
    }

    if (parentClass.includes('Transformer')) {
      return 'Transformers can only connect to Main Switchboards (MSB). All MSBs are already allocated or none exist.';
    } else if (parentClass.includes('Main_Switchboard')) {
      return 'MSBs can connect to MSSBs or Distribution Boards. All compatible switchboards are already allocated or none exist.';
    } else if (parentClass.includes('Mechanical_Services_Switchboard')) {
      return 'MSSBs can connect to DDC Panels or Distribution Boards. All compatible switchboards are already allocated or none exist.';
    }

    return 'No compatible switchboards available for this parent type.';
  };

  // Computed property for new switchboard location path
  const newSwitchboardLocationPath = computed(() => {
    if (!newSwitchboard.value.space_id) {return [];}
  
    // Find the space in the hierarchy
    const space = spaceOptions.value.find((s) => s.value === newSwitchboard.value.space_id);
    if (!space) {return [];}
  
    // Build breadcrumb path
    const path = [];
    // The space label already contains the full path
    const parts = space.label.split(' > ');
    return parts;
  });

  // Open location picker for new switchboard
  // openLocationPickerForSwitchboard moved to useFormHelpers composable

  // Confirm adding switchboard
  const confirmAddSwitchboard = async () => {
    await createNewSwitchboard();
  };

  // Add existing switchboard to electrical tree
  const addExistingSwitchboard = async () => {
    try {
      const switchboard = availableSwitchboardsForParent.value.find((s) => s.id === selectedExistingSwitchboard.value);
      if (!switchboard) {
        throw new Error('Selected switchboard not found');
      }

      // If there's a parent node, create a relationship
      if (parentElectricalNode.value) {
        const response = await authenticatedFetch('/api/relationships', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            source_id: parentElectricalNode.value.data.id,
            source_type: 'App\\Models\\SiteEquipment',
            target_id: switchboard.id,
            target_type: 'App\\Models\\SiteEquipment',
            relationship_type: 'feeds',
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to create relationship');
        }
      }

      // Reload regardless of whether a relationship was created (root switchboard case)
      await loadElectricalHierarchy();
      await loadEquipmentList();
      showAddSwitchboard.value = false;

      $q.notify({
        type: 'positive',
        message: `Added ${switchboard.name} to electrical hierarchy`,
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to add switchboard to hierarchy',
      });
    }
  };

  // Create new switchboard
  const createNewSwitchboard = async () => {
    try {
      // Find the selected type object to get the brick_class
      const selectedType = switchboardTypes.value.find(
        (type) => type.value === newSwitchboard.value.type,
      );

      if (!selectedType || !selectedType.brick_class) {
        throw new Error('Invalid switchboard type selected');
      }

      const brickClass = selectedType.brick_class;

      // Create the equipment
      const spaceId = newSwitchboard.value.space_id;
      if (!spaceId) {
        throw new Error('Space ID is required to create equipment');
      }

      const newEquipment = await api.post(`/v2/sites/${props.site.id}/equipment`, {
        space_id: spaceId,
        name: newSwitchboard.value.name,
        description: newSwitchboard.value.description,
        brick_class: brickClass,
        properties: JSON.stringify(newSwitchboard.value.properties || {}),
      });
    
      // Create relationship to parent
      await api.post('/relationships', {
        source_id: parentElectricalNode.value.data.id,
        source_type: 'App\\Models\\SiteEquipment',
        target_id: newEquipment.id,
        target_type: 'App\\Models\\SiteEquipment',
        relationship_type: 'feeds',
      });

      await loadElectricalHierarchy();
      await loadHierarchyData(true);
      await nextTick();
      await loadEquipmentList();
      showAddSwitchboardDialog.value = false;

      $q.notify({
        type: 'positive',
        message: `Created and added ${newSwitchboard.value.name} to electrical hierarchy`,
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to create switchboard',
      });
    }
  };

  // Handle ElectricalHierarchyTree node right-click
  const handleElectricalNodeRightClick = ({ node, event }) => {
    // Right-click on electrical node
  
    // Show context menu with options for the electrical equipment
    const type = (node.type || node.equipment_type || '').toLowerCase();
  
    // Create context menu items based on node type
    const menuItems = [];
  
    if (type.includes('switchboard') || type.includes('msb') || type.includes('mssb')) {
      menuItems.push(
        { label: 'Edit Switchboard', icon: 'edit', action: () => editSwitchboard(node) },
        { label: 'Delete Switchboard', icon: 'delete', action: () => confirmDelete({ data: node, type: 'switchboard' }) },
      );
    }
  
    if (type.includes('ddc')) {
      menuItems.push(
        { label: 'View DDC Points', icon: 'memory', action: () => viewSwitchboardPoints(node) },
        { label: 'Edit DDC Panel', icon: 'edit', action: () => editSwitchboard(node) },
        { label: 'Delete DDC Panel', icon: 'delete', action: () => confirmDelete({ data: node, type: 'switchboard' }) },
      );
    }
  
    if (type.includes('distribution') || type.includes('db')) {
      menuItems.push(
        { label: 'Edit Distribution Board', icon: 'edit', action: () => editSwitchboard(node) },
        { label: 'Delete Distribution Board', icon: 'delete', action: () => confirmDelete({ data: node, type: 'switchboard' }) },
      );
    }
  
    // Always add refresh option
    menuItems.push(
      { separator: true },
      { label: 'Refresh Electrical Hierarchy', icon: 'refresh', action: () => loadElectricalHierarchy() },
    );
  
    // Show Quasar context menu
    $q.createComponent({
      component: {
        template: `
        <q-list dense>
          <template v-for="(item, index) in items" :key="index">
            <q-separator v-if="item.separator" />
            <q-item v-else clickable v-close-popup @click="item.action">
              <q-item-section avatar>
                <q-icon :name="item.icon" />
              </q-item-section>
              <q-item-section>{{ item.label }}</q-item-section>
            </q-item>
          </template>
        </q-list>
      `,
        setup() {
          return { items: menuItems };
        },
      },
    }).then((menu) => {
      $q.createComponent({
        component: {
          template: '<q-menu><component :is="menu" /></q-menu>',
          components: { menu },
        },
      }).then((contextMenu) => {
        contextMenu.$el.style.position = 'fixed';
        contextMenu.$el.style.left = event.clientX + 'px';
        contextMenu.$el.style.top = event.clientY + 'px';
        document.body.appendChild(contextMenu.$el);
      });
    });
  };

  // Process electrical data with collapsed/expanded state

  // ApexTree is now handled by the ApexTreeChart component

  // Fallback simple chart renderer
  const renderSimpleElectricalChart = (data) => {
    if (!electricalContainer.value) {return;}
  
    const createNodeHtml = (node, level = 0) => {
      let bgColor = '#9e9e9e';
      let textColor = '#ffffff';
    
      if (node.type === 'MSB') {
        bgColor = '#d32f2f';
      } else if (node.type === 'MSSB') {
        bgColor = '#f57c00';
      } else if (node.type === 'DDC_Panel' || node.type === 'DDC_Controller') {
        bgColor = '#fbc02d';
        textColor = '#000000';
      } else if (node.type === 'Distribution_Board' || node.type === 'DB') {
        bgColor = '#757575';
      }
    
      let html = `
      <div style="margin-left: ${level * 40}px; margin-bottom: 10px;">
        <div style="display: inline-block; background: ${bgColor}; color: ${textColor}; padding: 8px 16px; border-radius: 4px; cursor: pointer;"
             onclick="window.electricalNodeClick('${node.id}')">
          <strong>${node.type || 'Equipment'}</strong><br>
          ${node.name || 'Unnamed'}
          ${node.equipment_count ? `<br><small>${node.equipment_count} equipment</small>` : ''}
        </div>
      </div>
    `;
    
      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => {
          html += createNodeHtml(child, level + 1);
        });
      }
    
      return html;
    };
  
    let html = '<div style="padding: 20px; font-family: Arial, sans-serif;">';
    if (Array.isArray(data)) {
      data.forEach((node) => {
        html += createNodeHtml(node);
      });
    } else {
      html += createNodeHtml(data);
    }
    html += '</div>';
  
    electricalContainer.value.innerHTML = html;
  
    // Add click handler
    window.electricalNodeClick = (nodeId) => {
      $q.notify({
        message: `Node ${nodeId} clicked`,
        position: 'top',
        color: 'info',
        timeout: 2000,
      });
    };
  };




  const exportElectricalDiagram = () => {
    if (!electricalContainer.value) {
      $q.notify({
        type: 'warning',
        message: 'No diagram to export',
      });
      return;
    }
  
    // Could use html2canvas or similar library
    $q.notify({
      type: 'info',
      message: 'Export functionality coming soon',
    });
  };

  // Airflow Visualization Methods
  const loadAirflowPaths = async () => {
    loadingAirflow.value = true;
    try {
      const response = await authenticatedFetch(`/api/sites/${props.site.id}/airflow-paths`, {
        headers: {
          'Accept': 'application/json',
        },
      });
    
      if (!response.ok) {throw new Error('Failed to load airflow paths');}
    
      const data = await response.json();
      airflowData.value = data.data?.paths || data.data || [];
    
      // Convert to table format instead of rendering chart
      processAirflowDataForTable();
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load airflow paths',
      });
    } finally {
      loadingAirflow.value = false;
    }
  };

  const processAirflowDataForTable = () => {
    const tableData = [];
  
    const processEquipmentAndSpaces = (item, parentName = null) => {
      // Process AHUs
      if (item.brick_class?.includes('Air_Handling_Unit') || item.brick_class?.includes('AHU')) {
        tableData.push({
          id: item.id,
          type: 'AHU',
          name: item.name,
          serves: item.supplies?.length ? `${item.supplies.length} connections` : 'No connections',
          fed_by: 'Primary Air',
          location: item.space?.name || 'N/A',
        });
      
        // Process supplies (VAVs, zones, spaces)
        if (item.supplies && Array.isArray(item.supplies)) {
          item.supplies.forEach((supply) => {
            if (supply.type === 'space') {
              tableData.push({
                id: supply.id,
                type: 'Space',
                name: supply.name || `Space ${supply.id}`,
                serves: '-',
                fed_by: supply.via_zone ? `${item.name} → ${supply.via_zone}` : item.name,
                location: supply.level ? `${supply.building} > ${supply.level}` : 'N/A',
              });
            } else {
              // Recursively process equipment
              processEquipmentAndSpaces(supply, item.name);
            }
          });
        }
      }
      // Process Zone Sections
      else if (item.brick_class?.includes('Zone_Section') || item.brick_class?.includes('AHU_Zone')) {
        tableData.push({
          id: item.id,
          type: 'Zone Section',
          name: item.name,
          serves: item.supplies?.length ? `${item.supplies.length} connections` : 'No connections',
          fed_by: parentName || 'AHU',
          location: item.space?.name || 'N/A',
        });
      
        // Process supplies (spaces)
        if (item.supplies && Array.isArray(item.supplies)) {
          item.supplies.forEach((supply) => {
            if (supply.type === 'space') {
              tableData.push({
                id: supply.id,
                type: 'Space',
                name: supply.name || `Space ${supply.id}`,
                serves: '-',
                fed_by: `${parentName || 'AHU'} → ${item.name}`,
                location: supply.level ? `${supply.building} > ${supply.level}` : 'N/A',
              });
            } else {
              processEquipmentAndSpaces(supply, item.name);
            }
          });
        }
      }
      // Process VAVs
      else if (item.brick_class?.includes('VAV') || item.brick_class?.includes('Variable_Air_Volume')) {
        tableData.push({
          id: item.id,
          type: 'VAV',
          name: item.name,
          serves: item.supplies?.length ? `${item.supplies.length} spaces` : 'No spaces',
          fed_by: parentName || 'AHU',
          location: item.space?.name || 'N/A',
        });
      
        // Process supplies (spaces)
        if (item.supplies && Array.isArray(item.supplies)) {
          item.supplies.forEach((supply) => {
            if (supply.type === 'space') {
              tableData.push({
                id: supply.id,
                type: 'Space',
                name: supply.name || `Space ${supply.id}`,
                serves: '-',
                fed_by: item.name,
                location: supply.level ? `${supply.building} > ${supply.level}` : 'N/A',
              });
            }
          });
        }
      }
    };
  
    if (airflowData.value && Array.isArray(airflowData.value)) {
      airflowData.value.forEach((item) => {
        processEquipmentAndSpaces(item);
      });
    }
  
    airflowTableData.value = tableData;
  };

  const getAirflowTypeColor = (type) => {
    const colors = {
      'AHU': 'blue',
      'VAV': 'cyan',
      'Space': 'light-blue',
      'FCU': 'teal',
      'Exhaust': 'grey',
    };
    return colors[type] || 'grey';
  };

  // Airflow visualization removed - using table view instead

  // Airflow zoom methods removed - no longer needed with table view

  // Switchboard Management Methods
  const showAddMSBDialog = () => {
    resetMSBForm();
    showMSBDialog.value = true;
  };

  const showAddMSSBDialog = () => {
    resetMSSBForm();
    // Ensure we have MSB data loaded
    if (msbOptions.value.length === 0) {
      $q.notify({
        type: 'warning',
        message: 'Please add at least one MSB first',
      });
      return;
    }
    showMSSBDialog.value = true;
  };

  const showAddDDCDialog = () => {
    resetDDCForm();
    // Ensure we have MSSB data loaded
    if (mssbOptions.value.length === 0) {
      $q.notify({
        type: 'warning',
        message: 'Please add at least one MSSB first',
      });
      return;
    }
    showDDCDialog.value = true;
  };

  const resetMSBForm = () => {
    msbForm.value = {
      name: '',
      description: '',
      voltage_rating: '415V',
      current_rating: '',
      manufacturer: '',
      model: '',
      location: null,
    };
  };

  const resetMSSBForm = () => {
    mssbForm.value = {
      parent_msb: null,
      name: '',
      description: '',
      current_rating: '',
      service_type: null,
      location: null,
    };
  };

  const resetDDCForm = () => {
    ddcForm.value = {
      parent_mssb: null,
      name: '',
      description: '',
      number_of_points: 0,
      controller_type: null,
      location: null,
    };
  };

  const saveMSB = async () => {
    savingMSB.value = true;
    try {
      // Prepare MSB data from form
      const msbData = {
        name: msbForm.value.name,
        description: msbForm.value.description,
        voltage_rating: msbForm.value.voltage_rating,
        current_rating: msbForm.value.current_rating,
        manufacturer: msbForm.value.manufacturer,
        model: msbForm.value.model,
        location: msbForm.value.location,
      };

      // Use electrical API composable to create MSB
      await electricalApi.createMSB(msbData);

      showMSBDialog.value = false;
      await loadElectricalHierarchy();
    } catch (error) {
      console.error('Failed to save MSB:', error);
    } finally {
      savingMSB.value = false;
    }
  };

  const saveMSSB = async () => {
    savingMSSB.value = true;
    try {
      // Prepare MSSB data from form
      const mssbData = {
        name: mssbForm.value.name,
        description: mssbForm.value.description,
        current_rating: mssbForm.value.current_rating,
        service_type: mssbForm.value.service_type,
        parent_msb: mssbForm.value.parent_msb,
        location: mssbForm.value.location,
      };

      // Use electrical API composable to create MSSB
      await electricalApi.createMSSB(mssbData);

      showMSSBDialog.value = false;
      await loadElectricalHierarchy();
    } catch (error) {
      console.error('Failed to save MSSB:', error);
    } finally {
      savingMSSB.value = false;
    }
  };

  const saveDDC = async () => {
    savingDDC.value = true;
    try {
      // Prepare DDC data from form
      const ddcData = {
        name: ddcForm.value.name,
        description: ddcForm.value.description,
        number_of_points: ddcForm.value.number_of_points,
        controller_type: ddcForm.value.controller_type,
        parent_mssb: ddcForm.value.parent_mssb,
        location: ddcForm.value.location,
      };

      // Use electrical API composable to create DDC
      await electricalApi.createDDC(ddcData);

      showDDCDialog.value = false;
      await loadElectricalHierarchy();
    } catch (error) {
      console.error('Failed to save DDC:', error);
    } finally {
      savingDDC.value = false;
    }
  };

  const editNode = () => {
    if (!selectedElectricalNode.value) {return;}
  
    $q.notify({
      type: 'info',
      message: `Edit functionality for ${selectedElectricalNode.value.name} coming soon`,
    });
  };

  const deleteNode = async () => {
    if (!selectedElectricalNode.value) {return;}
  
    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete ${selectedElectricalNode.value.name}?`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        const response = await authenticatedFetch(`/api/equipment/${selectedElectricalNode.value.id}`, {
          method: 'DELETE',
        });
      
        if (!response.ok) {throw new Error('Failed to delete equipment');}
      
        $q.notify({
          type: 'positive',
          message: 'Equipment deleted successfully',
        });
      
        await loadElectricalHierarchy();
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to delete equipment: ' + error.message,
        });
      }
    });
  };

  // Use composable's export/import functions
  const downloadTTL = exportImport.downloadTTL;
  const showRawTTL = async () => {
    await exportImport.showRawTTL();
    tab.value = 'export'; // Switch to export tab after loading
  };
  const copyTTLURL = exportImport.copyTTLURL;

  const showTemplateManager = () => {
    // Opening Template Manager
    showTemplateManagerDialog.value = true;
    loadTemplates();
    loadCompoundTemplates(); // Also load compound templates
  };

  const onTemplateManagerClose = () => {
    // Refresh compound templates when template manager closes
    // in case new templates were created
    loadCompoundTemplates();
  };

  // NOTE: showAddBuildingDialog, showAddLevelDialog, showAddSpaceDialog moved to HierarchyManager component

  // NOTE: showAddEquipmentDialogForNode moved to EquipmentManager component
  // NOTE: showAddDeviceDialog, addDevice, createCompoundDevice, onTemplateSelect, onDeviceTemplateSelect, onEditDeviceTemplateSelect moved to EquipmentManager component
  // NOTE: showAddPointDialogForNode, showAddPointDialog moved to EquipmentManager component

  const showAddSwitchboardDialogOld = (node) => {
    $q.notify({
      type: 'info',
      message: 'Add Switchboard - Implementation in progress',
    });
  };

  // Context Menu Methods
  const showContextMenu = (event, node) => {
    // Debug logging to identify node types
  
    contextMenuTarget.value = event.currentTarget;
    contextMenuNode.value = node;
    showContextMenuFlag.value = true;
  };

  // Template Management Methods
  const hasTemplateUpdate = (node) => {
    // Use composable to check for template updates
    if (node.data && node.type === 'equipment') {
      return templateManagement.hasTemplateUpdate(node.data);
    }
    return false;
  };

  const getTemplateTooltip = (node) => {
    // Use composable to get template tooltip
    if (node.data && node.type === 'equipment') {
      return templateManagement.getTemplateTooltip(node.data);
    }
    return '';
  };

  const saveAsTemplate = (node) => {
    // Saving as template
  
    // Prepare template form
    templateForm.value = {
      name: `${node.label} Template`,
      description: '',
      type: node.type,
      includeDevices: true,
      includePoints: true,
      scope: 'site',
      itemData: node.data,
      deviceCount: 0,
      pointCount: 0,
    };
  
    // Count devices and points for preview
    if ((node.type === 'equipment' || node.type === 'zone') && node.children) {
      const devices = node.children.filter((c) => c.type === 'device');
      templateForm.value.deviceCount = devices.length;
    
      // Count points in equipment/zone and devices
      let pointCount = node.children.filter((c) => c.type === 'point').length;
      devices.forEach((device) => {
        if (device.children) {
          pointCount += device.children.filter((c) => c.type === 'point').length;
        }
      });
      templateForm.value.pointCount = pointCount;
    } else if (node.type === 'device' && node.children) {
      templateForm.value.pointCount = node.children.filter((c) => c.type === 'point').length;
    }
  
    showSaveAsTemplateDialog.value = true;
  };

  const confirmSaveAsTemplate = async () => {
    if (!templateForm.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Template name is required',
      });
      return;
    }

    savingTemplate.value = true;
    try {
      let endpoint;
      let requestBody;

      // Check if this is a compound device that should be saved as a compound template
      const node = contextMenuNode.value || selectedNode.value;

      if (templateForm.value.type === 'zone') {
        endpoint = `/api/equipment/${templateForm.value.itemData.id}/save-as-template`;
        requestBody = {
          name: templateForm.value.name,
          description: templateForm.value.description,
          make_global: false,
        };
      } else if (templateForm.value.type === 'equipment') {
        endpoint = `/api/equipment/${templateForm.value.itemData.id}/save-as-template`;
        requestBody = {
          name: templateForm.value.name,
          description: templateForm.value.description,
          make_global: false,
        };
      } else if (templateForm.value.type === 'device') {
        // For devices, use the v2 device endpoint
        endpoint = `/api/v2/sites/${props.site.id}/devices/${templateForm.value.itemData.id}/save-as-template`;

        // Check if this is a compound device using the same logic as isCompoundDevice function
        const isCompound = !!(node && (node.data?.is_compound || node.isCompound));

        // Debug logging to understand compound device detection

        requestBody = {
          name: templateForm.value.name,
          description: templateForm.value.description,
          make_global: false,
          include_compound_structure: isCompound, // This flag tells the backend to create a compound template
        };
      } else {
        // Default case
        endpoint = `/api/equipment/${templateForm.value.itemData.id}/save-as-template`;
        requestBody = {
          name: templateForm.value.name,
          description: templateForm.value.description,
          make_global: false,
        };
      }

      // Only send minimal data - backend handles everything else
      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save template');
      }

      const result = await response.json();

      $q.notify({
        type: 'positive',
        message: `Template "${templateForm.value.name}" saved successfully`,
        icon: 'check_circle',
      });

      showSaveAsTemplateDialog.value = false;

      // Refresh templates based on what was saved
      if (templateForm.value.type === 'equipment' || templateForm.value.type === 'zone') {
        await loadEquipmentTemplates();
      } else if (templateForm.value.type === 'device') {
        // Check if the device being saved is a compound device
        const node = contextMenuNode.value || selectedNode.value;
        const isCompound = !!(node && (node.data?.is_compound || node.isCompound));

        if (isCompound) {
          // Compound device - refresh compound templates
          await loadCompoundTemplates();
        } else {
          // Regular device - refresh device templates
          await loadTemplates();
        }
      } else {
        await loadTemplates();
      }

    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to save template',
      });
    } finally {
      savingTemplate.value = false;
    }
  };

  const syncWithTemplate = async (node) => {
    if (!node.data) {return;}

    try {
      // Use composable with API callback
      const result = await templateManagement.syncWithTemplate(node.data, async (params) => {
        // API callback to sync the entity
        return await templateApi.syncWithTemplate(node.type, node.data.id, node.data.template_id);
      });

      if (result) {
        // Reload hierarchy to show updated data
        await loadHierarchyData();
      }
    } catch (error) {
      console.error('Failed to sync with template:', error);
    }
  };

  const detachFromTemplate = async (node) => {
    if (!node.data) {return;}

    try {
      // Use composable with API callback
      const result = await templateManagement.detachFromTemplate(node.data, async (params) => {
        // API callback to detach the entity
        return await templateApi.detachFromTemplate(node.type, node.data.id);
      });

      if (result) {
        // Clear template update cache for this item
        const cacheKey = `${node.type}_${node.data?.id}`;
        delete templateUpdateCache.value[cacheKey];

        // Reload hierarchy to show updated data
        await loadHierarchyData();
      }
    } catch (error) {
      console.error('Failed to detach from template:', error);
    }
  };

  const checkForTemplateUpdates = async (equipment) => {
    // Use composable to check for updates
    return templateManagement.hasTemplateUpdate(equipment);
  };

  const loadEquipmentTemplates = async () => {
    // Use composable to load equipment templates
    const templates = await templateApi.loadEquipmentTemplates();
    equipmentTemplates.value = templates;
  };

  // API Methods
  const loadHierarchyData = async (bypassCache = false) => {
    if (!props.site?.id) {
      return;
    }

    try {
      // Clear local state before refreshing to force complete rebuild
      if (bypassCache) {
        localHierarchyData.value = null;
        treeNodes.value = [];
      }

      // Only set loading to true if it's not already true (avoid redundant setting)
      if (!loadingHierarchy.value) {
        loadingHierarchy.value = true;
        await nextTick(); // Ensure spinner renders if this is called independently
      }

      const headers = {
        'Accept': 'application/json',
      };

      // Add cache bypass header if requested
      if (bypassCache) {
        headers['X-Bypass-Cache'] = 'true';
      }

      const response = await authenticatedFetch(`/api/v2/sites/${props.site.id}/hierarchy`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const hierarchyData = data.data || data;
      localHierarchyData.value = hierarchyData;
      buildTreeNodes();

    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load hierarchy: ${error.message}`,
      });
    } finally {
      loadingHierarchy.value = false;
    }
  };

  const loadEquipmentList = async () => {
    if (!props.site?.id) {return;}

    // Use equipment API composable to load equipment list
    await equipmentApi.loadEquipmentList();
  };

  const loadSwitchboards = async () => {
  // Loading switchboards
  };

  const loadSwitchboardTypes = async () => {
    try {
      // API helper already unwraps {success: true, data: [...]} to just [...]
      const response = await api.get('/brick-schema/switchboard-types');
      switchboardTypes.value = response;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load switchboard types from TTL file',
      });
    }
  };

  const buildTreeNodes = () => {
    if (!localHierarchyData.value?.buildings) {
      treeNodes.value = [];
      return;
    }

    // Call composable to build tree structure
    treeNodes.value = treeBuilder.buildTreeNodes(
      localHierarchyData.value,
      props.site,
      expandedNodes,
      preservingTreeState,
    );
  };

  // Template Manager Methods
  const loadTemplates = async () => {
    // Use composable to load templates
    await templateApi.loadTemplates();
  };

  const editTemplateFromManager = (template) => {
    if (!template || !template.id) {
      $q.notify({
        type: 'negative',
        message: 'Invalid template selected',
      });
      return;
    }
  
    // Opening template editor
    selectedTemplateId.value = template.id;
    selectedTemplateType.value = template.template_type;
    showTemplateEditor.value = true;
  };

  const toggleTemplateStatus = async (template) => {
    $q.notify({
      type: 'info',
      message: 'Template status management will be implemented in the future',
    });
  };

  const showTemplateDetails = (template) => {
    let detailsHtml = `
    <div class="q-mb-sm"><strong>Name:</strong> ${template.name}</div>
    <div class="q-mb-sm"><strong>Type:</strong> ${template.template_type}</div>
    <div class="q-mb-sm"><strong>BRICK Class:</strong> ${formatBrickClass(template.brick_class)}</div>
    <div class="q-mb-sm"><strong>Description:</strong> ${template.description || 'No description'}</div>
  `;

    if (template.template_type === 'compound') {
      detailsHtml += `
      <div class="q-mb-sm"><strong>Components:</strong> ${template.component_count || 0}</div>
      <div class="q-mb-sm"><strong>Total Points:</strong> ${template.total_points || 0}</div>
    `;
    }

    detailsHtml += `
    <div class="q-mb-sm"><strong>Usage Count:</strong> ${template.usage_count || 0}</div>
    <div class="q-mb-sm"><strong>Status:</strong> ${template.is_active ? 'Active' : 'Archived'}</div>
    <div class="q-mb-sm"><strong>Created:</strong> ${new Date(template.created_at).toLocaleDateString()}</div>
  `;

    $q.dialog({
      title: 'Template Details',
      message: detailsHtml,
      html: true,
      ok: 'Close',
    });
  };

  // View compound template details
  const viewCompoundTemplate = (template) => {
    $q.dialog({
      title: `Compound Template: ${template.name}`,
      message: `
      <div class="q-mb-sm"><strong>Description:</strong> ${template.description || 'No description'}</div>
      <div class="q-mb-sm"><strong>BRICK Class:</strong> ${template.brick_class}</div>
      <div class="q-mb-sm"><strong>Components:</strong> ${template.component_count || 0}</div>
      <div class="q-mb-sm"><strong>Total Points:</strong> ${template.total_points || 0}</div>
      <div class="q-mb-sm"><strong>Usage Count:</strong> ${template.usage_count || 0}</div>
    `,
      html: true,
      ok: 'Close',
    });
  };

  // Delete template function
  const deleteTemplate = async (template) => {
    try {
      // Use composable to delete template (handles confirmation and API call)
      await templateApi.deleteTemplate(template);

      // Clear selected template if it was the one that was deleted
      if (newEquipment.value.selectedTemplate &&
        (newEquipment.value.selectedTemplate.value === template.id ||
          newEquipment.value.selectedTemplate.id === template.id)) {
        newEquipment.value.selectedTemplate = null;
        newEquipment.value.equipment_template_id = null;
        // Reset fields that might have been populated from template
        newEquipment.value.brick_class = '';
        newEquipment.value.description = '';
      }

      // Also check device template selection
      if (newDevice.value.selectedTemplate &&
        (newDevice.value.selectedTemplate.value === template.id ||
          newDevice.value.selectedTemplate.id === template.id)) {
        newDevice.value.selectedTemplate = null;
        newDevice.value.device_template_id = null;
        // Reset fields that might have been populated from template
        newDevice.value.brick_class = '';
        newDevice.value.manufacturer = '';
        newDevice.value.model_number = '';
      }

      // Reload equipment templates
      await loadEquipmentTemplates();
    } catch (error) {
      // Error handling is done in composable, but handle usage dialog here
      if (error.usage) {
        // Show usage locations dialog
        usageLocations.value = error.usage;
        usageTemplate.value = template;
        await nextTick();
        showUsageLocationsDialog.value = true;
      }
    }
  };

  // Format template type for display
  const formatTemplateType = (type) => {
    const typeMap = {
      'compound': 'Compound',
      'equipment': 'Equipment',
      'device': 'Device',
    };
    return typeMap[type] || type;
  };

  // Get color for template type badge
  const getTemplateTypeColor = (type) => {
    const colorMap = {
      'compound': 'purple',
      'equipment': 'primary',
      'device': 'orange',
    };
    return colorMap[type] || 'grey';
  };

  // Template Editor Event Handlers
  const onTemplateUpdated = (updatedTemplate) => {
    // Template updated

    // Refresh template list to show updated template
    loadTemplates();

    // Show success notification
    $q.notify({
      type: 'positive',
      message: `Template "${updatedTemplate.name}" updated successfully`,
      icon: 'check_circle',
    });

    // Refresh hierarchy data to show any equipment changes
    refreshData();
  };

  const onTemplateEditorClose = (isOpen) => {
    if (!isOpen) {
      // Refresh compound templates when template editor closes
      // in case new compound templates were created or modified
      loadCompoundTemplates();
    }
  };

  // ============================================================
  // CONTROLLER ASSIGNMENT METHODS
  // ============================================================
  // Note: isController is now imported from useTemplateProtection composable

  const openAssignControllerDialog = (node) => {
    selectedEquipmentForController.value = node.data;
    showAssignControllerDialog.value = true;
  };

  const unassignFromController = async (node) => {
    try {
      await unassignControllerApi(node.data.id, false);
      $q.notify({
        type: 'positive',
        message: 'Controller unassigned successfully',
        timeout: 2000,
      });
      await refreshData();
    } catch (error) {
      console.error('Failed to unassign controller:', error);
    }
  };

  const openControllerIoUsageDialog = async (node) => {
    // Fetch full controller data with all relationships
    try {
      const response = await authenticatedFetch(`/api/v2/sites/${siteId.value}/equipment/${node.data.id}`);
      if (response.ok) {
        const data = await response.json();
        selectedControllerForUsage.value = data.data;
        showControllerIoUsageDialog.value = true;
      } else {
        $q.notify({
          type: 'negative',
          message: 'Failed to load controller data',
        });
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Error loading controller data',
      });
    }
  };

  // Check if node can have a controller added to it
  const canAddController = (node) => {
    if (!node?.data?.brick_class) {return false;}

    // DDC boards/panels can have controllers added
    const isDDCBoard = node.data.brick_class.includes('DDC_Panel') ||
      node.data.brick_class.includes('BMS_Panel');

    // Equipment can have integrated controllers (AHUs, VAVs, Chillers, etc.)
    const isEquipment = node.data.brick_class.includes('Air_Handling_Unit') ||
      node.data.brick_class.includes('Variable_Air_Volume_Box') ||
      node.data.brick_class.includes('Chiller') ||
      node.data.brick_class.includes('Boiler') ||
      node.data.brick_class.includes('Pump') ||
      node.data.brick_class.includes('Fan_Coil_Unit');

    // Node itself should not already be a controller
    const isNotController = !isController(node);

    return (isDDCBoard || isEquipment) && isNotController;
  };

  const openAddControllerDialog = (node) => {
    selectedParentForController.value = node.data;
    showAddControllerDialog.value = true;
  };

  const handleControllerAdded = async (newController) => {
    await refreshData();
    showAddControllerDialog.value = false;

    // Expand the parent node to show the new controller
    if (selectedParentForController.value?.id) {
      const parentKey = `${selectedParentForController.value.brick_class}-${selectedParentForController.value.id}`;
      if (!expandedEquipment.value.includes(parentKey)) {
        expandedEquipment.value.push(parentKey);
      }
    }
  };

  const handleControllerAssigned = async () => {
    await refreshData();
  };

  const onTemplateDeleted = (deletedTemplateId) => {
    // Template deleted

    // Clear selected template if it was the one that was deleted
    if (newEquipment.value.selectedTemplate &&
      (newEquipment.value.selectedTemplate.value === deletedTemplateId ||
        newEquipment.value.selectedTemplate.id === deletedTemplateId)) {
      newEquipment.value.selectedTemplate = null;
      newEquipment.value.equipment_template_id = null;
      // Reset fields that might have been populated from template
      newEquipment.value.brick_class = '';
      newEquipment.value.description = '';
    }

    // Also check device template selection
    if (newDevice.value.selectedTemplate &&
      (newDevice.value.selectedTemplate.value === deletedTemplateId ||
        newDevice.value.selectedTemplate.id === deletedTemplateId)) {
      newDevice.value.selectedTemplate = null;
      newDevice.value.device_template_id = null;
      // Reset fields that might have been populated from template
      newDevice.value.brick_class = '';
      newDevice.value.manufacturer = '';
      newDevice.value.model_number = '';
    }

    // Refresh template list to remove deleted template
    loadTemplates();
    loadEquipmentTemplates();

    // Show success notification
    $q.notify({
      type: 'positive',
      message: 'Template deleted successfully',
      icon: 'delete',
    });

    // Refresh hierarchy data to show any equipment changes
    refreshData();

    // Close template editor dialog
    showTemplateEditor.value = false;
    selectedTemplateId.value = null;
    selectedTemplateType.value = null;
  };

  // Methods for deleting usage items from the usage locations dialog
  const deleteUsageItem = async (item) => {
    // Delete individual usage item
    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      // Mark this item as being deleted
      item.deleting = true;
      deletingUsageItem.value = true;

      try {
        // Determine the correct endpoint based on item type
        let endpoint = '';
        if (item.type === 'equipment' || item.type === 'Equipment') {
          endpoint = `/api/v2/sites/${props.site.id}/equipment/${item.id}`;
        } else if (item.type === 'device' || item.type === 'Device') {
          endpoint = `/api/v2/sites/${props.site.id}/devices/${item.id}`;
        } else if (item.type === 'zone' || item.type === 'Zone') {
          endpoint = `/api/v2/sites/${props.site.id}/equipment/${item.id}`;
        } else {
          throw new Error(`Unknown item type: ${item.type}`);
        }

        // Delete the item
        const response = await authenticatedFetch(endpoint, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to delete item');
        }

        // Remove the item from the usage locations list
        const index = usageLocations.value.findIndex((loc) => loc.id === item.id);
        if (index > -1) {
          usageLocations.value.splice(index, 1);
        }

        $q.notify({
          type: 'positive',
          message: `${item.name} deleted successfully`,
        });

        // If all items are now deleted, we can retry deleting the template
        if (usageLocations.value.length === 0 && usageTemplate.value) {
          $q.notify({
            type: 'info',
            message: 'All items removed. You can now delete the template.',
            icon: 'info',
          });
        }

      } catch (error) {
        $q.notify({
          type: 'negative',
          message: `Failed to delete ${item.name}: ${error.message}`,
        });
      } finally {
        item.deleting = false;
        deletingUsageItem.value = false;
      }
    });
  };

  const confirmDeleteAllUsages = () => {
    // Confirm deletion of all usage items
    const itemCount = usageLocations.value.length;

    $q.dialog({
      title: 'Delete All Items',
      message: `Are you sure you want to delete ALL ${itemCount} ${itemCount === 1 ? 'item' : 'items'} using this template?`,
      html: true,
      cancel: true,
      persistent: true,
      ok: {
        label: `Delete All ${itemCount} Items`,
        color: 'negative',
      },
    }).onOk(async () => {
      await deleteAllUsages();
    });
  };

  const deleteAllUsages = async () => {
    // Delete all usage items
    deletingAllUsages.value = true;

    const errors = [];
    const successes = [];

    try {
      // Process deletions in parallel batches to avoid overwhelming the server
      const batchSize = 5;
      for (let i = 0; i < usageLocations.value.length; i += batchSize) {
        const batch = usageLocations.value.slice(i, i + batchSize);

        await Promise.all(batch.map(async (item) => {
          try {
            // Determine the correct endpoint
            let endpoint = '';
            if (item.type === 'equipment' || item.type === 'Equipment') {
              endpoint = `/api/v2/sites/${props.site.id}/equipment/${item.id}`;
            } else if (item.type === 'device' || item.type === 'Device') {
              endpoint = `/api/v2/sites/${props.site.id}/devices/${item.id}`;
            } else if (item.type === 'zone' || item.type === 'Zone') {
              endpoint = `/api/v2/sites/${props.site.id}/equipment/${item.id}`;
            } else {
              throw new Error(`Unknown item type: ${item.type}`);
            }

            const response = await authenticatedFetch(endpoint, {
              method: 'DELETE',
            });

            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.message || 'Failed to delete');
            }

            successes.push(item.name);
          } catch (error) {
            errors.push({ item: item.name, error: error.message });
          }
        }));
      }

      // Update the usage locations list
      usageLocations.value = usageLocations.value.filter((item) =>
        !successes.includes(item.name),
      );

      // Show results
      if (successes.length > 0) {
        $q.notify({
          type: 'positive',
          message: `Successfully deleted ${successes.length} ${successes.length === 1 ? 'item' : 'items'}`,
          timeout: 3000,
        });
      }

      if (errors.length > 0) {
        $q.notify({
          type: 'negative',
          message: `Failed to delete ${errors.length} ${errors.length === 1 ? 'item' : 'items'}: ${errors[0].error}`,
          timeout: 5000,
        });
      }

      // If all items are deleted, show success
      if (usageLocations.value.length === 0 && usageTemplate.value) {
        $q.notify({
          type: 'info',
          message: 'All items removed. You can now delete the template.',
          icon: 'check_circle',
        });
      }

    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to delete items: ${error.message}`,
      });
    } finally {
      deletingAllUsages.value = false;
    }
  };

  const retryDeleteTemplate = async () => {
    // Retry deleting the template after all usage items have been removed
    if (usageTemplate.value) {
      deletingTemplate.value = true;
      try {
        await deleteTemplate(usageTemplate.value);
        // If successful, close the dialog
        showUsageLocationsDialog.value = false;
        usageTemplate.value = null;
        usageLocations.value = [];
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: `Failed to delete template: ${error.message}`,
        });
      } finally {
        deletingTemplate.value = false;
      }
    }
  };

  const closeUsageDialog = () => {
    // Close the usage dialog and clear data
    showUsageLocationsDialog.value = false;
    usageTemplate.value = null;
    usageLocations.value = [];
    deletingUsageItem.value = false;
    deletingAllUsages.value = false;
    deletingTemplate.value = false;

    // Refresh data to show any changes
    refreshData();
  };

  // Template protection functions (isTemplatedEquipment, isTemplatedDevice, isTemplatedPoint,
  // canAddPointsToEquipment, getTemplateInfo) now imported from useTemplateProtection composable

  const showTemplateRestrictedDialog = (node) => {
    const templateInfo = getTemplateInfo(node);
    if (!templateInfo) {return;}
  
    const itemType = node.type === 'device' ? 'device' : 'equipment';
  
    $q.dialog({
      title: `Templated ${itemType.charAt(0).toUpperCase() + itemType.slice(1)} Restriction`,
      message: `
      <div class="q-mb-sm">
        <q-icon name="info" color="orange" size="md" class="q-mr-sm" />
        This ${itemType} was created from a template and cannot be modified directly.
      </div>
      <div class="q-mb-sm"><strong>Template:</strong> ${templateInfo.templateName}</div>
      <div class="text-body2 text-grey-7">
        To modify this ${itemType}'s configuration, points, or properties, you must edit the template. 
        This ensures consistency across all ${itemType} instances using this template.
      </div>
    `,
      html: true,
      ok: {
        label: 'Template Actions',
        color: 'orange',
        icon: 'settings',
      },
      cancel: {
        label: 'Cancel',
        flat: true,
      },
    }).onOk(() => {
      showTemplateActions(node);
    });
  };

  const showTemplateActions = (node) => {
    const templateInfo = getTemplateInfo(node);
    if (!templateInfo) {
      $q.notify({
        type: 'negative',
        message: 'Template information not found for this item',
      });
      return;
    }

    $q.dialog({
      title: 'Template Actions',
      message: `
      <div class="q-mb-sm">
        <div class="text-subtitle2">${node.label}</div>
        <div class="text-caption text-grey-7">Template: ${templateInfo.templateName}</div>
      </div>
      <div class="text-body2 q-mb-md">
        Choose an action for this templated ${node.type}:
      </div>
    `,
      html: true,
      options: {
        type: 'radio',
        model: 'edit',
        items: [
          { label: 'Edit Template', value: 'edit', color: 'orange' },
          { label: 'Remove from Template (make independent)', value: 'remove', color: 'negative' },
        ],
      },
      cancel: true,
      persistent: false,
    }).onOk((action) => {
      if (action === 'edit') {
        editEquipmentTemplate(node);
      } else if (action === 'remove') {
        detachFromTemplate(node);
      }
    });
  };

  const editEquipmentTemplate = (node) => {
    // Edit equipment template
    const templateInfo = getTemplateInfo(node);
    // Extracted template info
    if (!templateInfo) {
      $q.notify({
        type: 'negative',
        message: 'Template information not found for this equipment',
      });
      return;
    }
  
    // Find the template in available templates by matching both ID and template type
    const template = availableTemplates.value.find((t) => 
      t.id === templateInfo.templateId && 
      t.template_type === templateInfo.templateType,
    );
    if (!template) {
      $q.notify({
        type: 'warning',
        message: 'Template not found. Loading templates...',
      });
      // Load templates and try again
      loadTemplates().then(() => {
        const foundTemplate = availableTemplates.value.find((t) => 
          t.id === templateInfo.templateId && 
          t.template_type === templateInfo.templateType,
        );
        if (foundTemplate) {
          editTemplateFromManager(foundTemplate);
        } else {
          $q.notify({
            type: 'negative',
            message: `Template could not be loaded (ID: ${templateInfo.templateId}, Type: ${templateInfo.templateType})`,
          });
        }
      });
      return;
    }
  
    // Open template editor
    editTemplateFromManager(template);
  };

  // Equipment Management Methods
  const addEquipment = () => {
    // Opening Add Equipment dialog
  
    // Clear previous selections but keep space_id null for new selection
    selectedSpace.value = null;
    newEquipment.value = {
      name: '',
      brick_class: '',
      description: '',
      equipment_template_id: null,
      selectedTemplate: null,
      space_id: null,
      parent_equipment_id: null,
      serves_space: null,
    };
  
    showEquipmentDialog.value = true;
  
    // Load templates if not already loaded
    if (availableTemplates.value.length === 0) {
      loadTemplates();
    }
  
    // Load equipment templates if not already loaded
    if (equipmentTemplates.value.length === 0) {
      loadEquipmentTemplates();
    }
  };

  // NOTE: Edit functions for hierarchy entities (editBuilding, editLevel, editSpace) moved to HierarchyManager component
  // NOTE: Edit functions for equipment entities (editEquipment, editDevice, editPoint) moved to EquipmentManager component

  // Handle device editing from I/O Usage Panel - delegates to equipment manager
  function handleEditDeviceFromIo(deviceId) {
    // Find the device in the current site data
    const device = findDeviceById(deviceId);
    if (device) {
      // Create a mock node for the equipment manager
      const mockNode = {
        data: device,
        label: device.name,
        type: 'device',
      };
      equipmentManager.value?.editDevice(mockNode);
    } else {
      $q.notify({
        type: 'warning',
        message: 'Device not found in current site data',
        icon: 'warning',
      });
    }
  }

  // Helper to find device by ID in current site structure
  function findDeviceById(deviceId) {
    for (const equip of props.site.equipment || []) {
      for (const device of equip.devices || []) {
        if (device.id === deviceId) {return device;}
      }
    }
    return null;
  }

  // Generic context menu handlers for unknown node types
  const viewNodeDetails = (node) => {
    $q.dialog({
      title: `Node Details: ${node.label || 'Unknown'}`,
      message: `
      <div>
        <p><strong>Type:</strong> ${node.type || 'undefined'}</p>
        <p><strong>Subtype:</strong> ${node.subtype || 'none'}</p>
        <p><strong>ID:</strong> ${node.id || 'none'}</p>
        <p><strong>BRICK Class:</strong> ${node.data?.brick_class || 'none'}</p>
        <p><strong>Data:</strong></p>
        <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; font-size: 11px; overflow-x: auto;">
${JSON.stringify(node.data || {}, null, 2)}
        </pre>
      </div>
    `,
      html: true,
      ok: true,
      class: 'node-details-dialog',
    });
  };

  const refreshNode = (node) => {
    $q.notify({
      type: 'info',
      message: `Refreshing ${node.label || 'node'}...`,
      icon: 'refresh',
    });
    // Trigger a refresh of the hierarchy
    loadHierarchyData();
  };

  // Delete functions with confirmation
  const confirmDelete = (node) => {
    // Confirm delete

    // If node has children, show cascade delete dialog
    if (node.children && node.children.length > 0) {
      // Build the error data structure that showCascadeDeleteDialog expects
      const errorData = {
        can_cascade: true,
        blocking_items: {
          children: {
            count: node.children.length,
            examples: node.children.slice(0, 5).map((child) => child.label),
            message: `${node.children.length} child item${node.children.length !== 1 ? 's' : ''}`,
          },
        },
      };

      showCascadeDeleteDialog(node, errorData);
      return;
    }

    // Check other delete restrictions (sites, templated equipment)
    if (!canDeleteNode(node)) {
      let message = `Cannot delete ${node.label}`;
      if (node.type === 'site') {
        message = 'Sites cannot be deleted through this interface';
      } else if (node.type === 'equipment' && isTemplatedEquipment(node)) {
        message = `Cannot delete templated equipment ${node.label}. Unlink from template first.`;
      } else if (node.type === 'device' && isTemplatedDevice(node)) {
        message = `Cannot delete templated device ${node.label}. Unlink from template first.`;
      }

      $q.notify({
        type: 'negative',
        message: message,
        icon: 'error',
      });
      return;
    }
  
    const entityType = node.type;
    const entityName = node.label;
  
    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete ${entityType} "${entityName}"? This action cannot be undone.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      await hierarchyOps.deleteHierarchyNode(node, false, {
        $q,
        apis: { buildingApi, equipmentApi, deviceApi, pointApi },
        authenticatedFetch,
        refreshData,
        showCascadeDeleteDialog,
      });
    });
  };

  // Show cascade delete confirmation dialog
  const showCascadeDeleteDialog = (node, errorData) => {
    // IMPORTANT: Defer dialog creation to avoid conflict with parent dialog's .onOk() callback
    // Quasar doesn't allow opening dialogs while another dialog callback is executing
    setTimeout(() => {
      // Build a detailed message about what will be deleted
      let detailMessage = '<div class="q-mb-md">This item has relationships that prevent deletion:</div>';

      if (errorData.blocking_items) {
        detailMessage += '<ul class="q-mb-md">';

        // Handle both array format and object format for blocking_items
        const itemsToProcess = Array.isArray(errorData.blocking_items)
          ? errorData.blocking_items.map((item, index) => [`Item ${index + 1}`, item])
          : Object.entries(errorData.blocking_items);

        for (const [itemType, itemData] of itemsToProcess) {
          // Handle multiple formats: object, array of items, or simple string
          if (itemData) {
            let count, examples, message;

            // Check if it's the new object format from backend
            if (typeof itemData === 'object' && !Array.isArray(itemData) && 'count' in itemData) {
              count = itemData.count;
              examples = itemData.examples || [];
              message = itemData.message || `${count} ${itemType}`;
            }
            // Legacy array format (array of item names)
            else if (Array.isArray(itemData) && itemData.length > 0) {
              count = itemData.length;
              examples = itemData.slice(0, 5);
              message = `${count} item(s)`;
            }
            // Simple string format (e.g., "2 child equipment")
            else if (typeof itemData === 'string') {
              count = 1; // We have at least one entry
              examples = [];
              message = itemData; // Use the string as-is
            } else {
              continue; // Skip invalid entries
            }

            if (count > 0) {
              // For simple string format from array, don't show the "Item X:" prefix
              const prefix = Array.isArray(errorData.blocking_items) ? '' : `<strong>${itemType}:</strong> `;
              detailMessage += `<li>${prefix}${message}`;
              if (examples.length > 0) {
                detailMessage += '<ul>';
                const displayExamples = examples.slice(0, 3);
                displayExamples.forEach((item) => {
                  detailMessage += `<li>${item}</li>`;
                });
                if (examples.length > 3) {
                  detailMessage += `<li><em>...and ${examples.length - 3} more</em></li>`;
                }
                detailMessage += '</ul>';
              }
              detailMessage += '</li>';
            }
          }
        }
        detailMessage += '</ul>';
      }
  
      detailMessage += '<div class="text-warning q-mt-md"><strong>Warning:</strong> Cascade delete will permanently remove all these related items. This action cannot be undone.</div>';

      $q.dialog({
        title: `Delete ${node.label} and All Related Items?`,
        message: detailMessage,
        html: true,
        cancel: {
          label: 'Cancel',
          color: 'primary',
          flat: true,
        },
        ok: {
          label: 'Delete All',
          color: 'negative',
          unelevated: true,
        },
        persistent: true,
      }).onOk(async () => {
        // User confirmed cascade delete
        await hierarchyOps.deleteHierarchyNode(node, true, {
          $q,
          apis: { buildingApi, equipmentApi, deviceApi, pointApi },
          authenticatedFetch,
          refreshData,
          showCascadeDeleteDialog,
        });
      }).onCancel(() => {
        // User cancelled - no action needed
      });
    }, 0); // End of setTimeout - 0ms delay allows current dialog callback to complete
  };

  const canDeleteNode = (node) => {
    // Note: This function now only checks for non-child-related restrictions
    // Children are handled separately in confirmDelete to allow cascade delete

    // Site nodes cannot be deleted
    if (node.type === 'site') {
      return false;
    }

    // Templated equipment/devices need special handling
    if (node.type === 'equipment' && isTemplatedEquipment(node)) {
      // Should be unlinked from template first
      return false;
    }

    if (node.type === 'device' && isTemplatedDevice(node)) {
      // Should be unlinked from template first
      return false;
    }

    return true;
  };

  // Legacy deleteEquipment function - redirect to confirmDelete
  const deleteEquipment = (equipment) => {
    // Delete equipment
    confirmDelete({
      type: 'equipment',
      label: equipment.name,
      data: equipment,
      children: equipment.devices || [],
    });
  };

  // Mark as Replacement handler (for quote context)
  const handleMarkAsReplacement = async (node) => {
    if (!props.quoteContext?.quote_id) {
      $q.notify({
        type: 'negative',
        message: 'Quote context is required to mark equipment as replacement',
      });
      return;
    }

    // Get quote equipment list to select replacement from
    const quoteEquipment = equipmentList.value.filter((eq) =>
      eq.quote_id === props.quoteContext.quote_id &&
      eq.lifecycle_stage === 'quote',
    );

    if (quoteEquipment.length === 0) {
      $q.notify({
        type: 'warning',
        message: 'No quote equipment available. Please create equipment in this quote first.',
      });
      return;
    }

    // Show dialog to select replacement equipment
    $q.dialog({
      title: 'Mark as Replacement',
      message: `Select which quote equipment will replace "${node.label}"`,
      options: {
        type: 'radio',
        model: null,
        items: quoteEquipment.map((eq) => ({
          label: `${eq.name} (${eq.brick_class || 'No class'})`,
          value: eq.id,
        })),
      },
      cancel: true,
    }).onOk(async (replacementEquipmentId) => {
      if (!replacementEquipmentId) {
        return;
      }

      try {
        await quoteService.markEquipmentAsReplacement(
          props.quoteContext.quote_id,
          node.data.id,
          { replaces_equipment_id: node.data.id },
        );

        $q.notify({
          type: 'positive',
          message: 'Equipment marked as replacement successfully',
        });

        // Refresh to show updated state
        await refreshData();
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: `Failed to mark as replacement: ${error.message}`,
        });
      }
    });
  };

  // Handle device selection from BOM sidebar
  const handleDeviceSelected = (deviceId) => {
    // Find the device in the tree and select/expand to it
    const findDeviceNode = (nodes) => {
      for (const node of nodes) {
        if (node.type === 'device' && node.data?.id === deviceId) {
          return node;
        }
        if (node.children) {
          const found = findDeviceNode(node.children);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    const deviceNode = findDeviceNode(treeNodes.value);
    if (deviceNode) {
      // Expand parent nodes to make device visible
      const expandParents = (node) => {
        if (node.parent) {
          if (!expandedNodes.value.includes(node.parent.id)) {
            expandedNodes.value.push(node.parent.id);
          }
          expandParents(node.parent);
        }
      };

      // Expand to device
      if (deviceNode.parent) {
        expandParents(deviceNode);
      }

      // Select the device node
      selectedNode.value = deviceNode.id;

      // Switch to hierarchy tab
      tab.value = 'hierarchy';

      $q.notify({
        type: 'info',
        message: `Navigated to device: ${deviceNode.label}`,
      });
    } else {
      $q.notify({
        type: 'warning',
        message: 'Device not found in current view',
      });
    }
  };

  const convertToTemplate = (equipment) => {
    // Converting to template
    $q.notify({
      type: 'info',
      message: `Convert ${equipment.name} to template`,
    });
  };

  // NOTE: Delete functions (deleteBuilding, deleteLevel, deleteSpace) moved to HierarchyManager component
  // NOTE: Delete functions (deleteEquipment, deleteDevice, deletePoint) moved to EquipmentManager component

  // Change device location function
  // State for device location change
  const deviceToMove = ref(null);

  const changeDeviceLocation = (node) => {
    // Store the device node that we're tracking the physical location for
    deviceToMove.value = node;

    // Check if the device already has a location_space_id set
    if (node.data?.location_space_id) {
      // Pre-select the current physical location in the picker
      selectedLocationForPicker.value = node.data.location_space_id;
    } else if (node.data?.properties?.physical_location) {
      // Fallback: check old properties field for backward compatibility
      const physicalLocation = node.data.properties.physical_location;
      if (physicalLocation?.type === 'space' && physicalLocation?.id) {
        selectedLocationForPicker.value = physicalLocation.id;
      } else {
        selectedLocationForPicker.value = null;
      }
    } else {
      selectedLocationForPicker.value = null;
    }

    // Set the mode for the location picker
    locationPickerMode.value = 'device-move';

    // Open the location picker dialog
    showLocationPicker.value = true;
  };

  const editTemplate = (template) => {
    // Editing template
    // If it's a templated equipment, we need to find the actual template
    if (template.template_id) {
      selectedTemplateId.value = template.template_id;
    } else {
      selectedTemplateId.value = template.id;
    }
    selectedTemplateType.value = 'equipment';
    showTemplateEditor.value = true;
  };

  const toggleEquipmentExpansion = (equipmentId) => {
    const index = expandedEquipment.value.indexOf(equipmentId);
    if (index > -1) {
      expandedEquipment.value.splice(index, 1);
    } else {
      expandedEquipment.value.push(equipmentId);
    }
  };

  // getPointIcon moved to useNodeRendering composable

  // NOTE: Form action methods (addBuilding, addLevel, addSpace) moved to HierarchyManager component

  const addEquipmentSubmit = async () => {
    // Validation - check if we're adding to space or parent equipment
    if (!newEquipment.value.space_id && !newEquipment.value.parent_equipment_id) {
      $q.notify({
        type: 'negative',
        message: 'Please select a space or parent equipment for the equipment',
      });
      return;
    }

    if (!newEquipment.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Equipment name is required',
      });
      return;
    }

    // If using a template, brick_class is optional (it comes from template)
    // If NOT using a template, brick_class is required
    if (!newEquipment.value.equipment_template_id && !newEquipment.value.brick_class) {
      $q.notify({
        type: 'negative',
        message: 'Equipment BRICK class is required',
      });
      return;
    }

    saving.value = true;
    try {
      let result;

      if (newEquipment.value.parent_equipment_id) {
        // Adding child equipment to parent equipment (e.g., damper to zone section)
        const equipmentData = {
          name: newEquipment.value.name,
          description: newEquipment.value.description || null,
          brick_class: newEquipment.value.brick_class,
          parent_equipment_id: newEquipment.value.parent_equipment_id,
        };

        // Check if in quote context
        if (props.quoteContext?.quote_id) {
          // Get building info from parent equipment's space
          let buildingId = null;
          let buildingLevelId = null;
          let parentSpaceId = null;

          // Find parent equipment in hierarchy to get building context
          if (localHierarchyData.value?.buildings) {
            outerLoop: for (const building of localHierarchyData.value.buildings) {
              if (building.levels) {
                for (const level of building.levels) {
                  if (level.spaces) {
                    for (const space of level.spaces) {
                      if (space.equipment) {
                        const parentEquipment = space.equipment.find(
                          (eq) => eq.id === newEquipment.value.parent_equipment_id,
                        );
                        if (parentEquipment) {
                          buildingId = building.id;
                          buildingLevelId = level.id;
                          parentSpaceId = space.id;
                          break outerLoop;
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          // Build request for quote equipment API
          const requestBody = {
            name: equipmentData.name,
            description: equipmentData.description,
            brick_class: equipmentData.brick_class,
            parent_equipment_id: equipmentData.parent_equipment_id,
            building_id: buildingId,
            template_id: newEquipment.value.equipment_template_id || null,
          };

          if (buildingLevelId) {
            requestBody.building_level_id = buildingLevelId;
          }

          if (parentSpaceId) {
            requestBody.space_id = parentSpaceId;
          }

          result = await quoteService.createQuoteEquipment(
            props.quoteContext.quote_id,
            requestBody,
          );
          result = result.data || result;

          $q.notify({
            type: 'positive',
            message: `Child equipment "${equipmentData.name}" created in quote context`,
          });
        } else {
          // Regular equipment creation (site context)
          result = await equipmentApi.createEquipment(equipmentData);
        }
      } else if (newEquipment.value.equipment_template_id) {
        // Creating equipment from template - use template instantiation endpoint
        const templateId = typeof newEquipment.value.equipment_template_id === 'number'
          ? newEquipment.value.equipment_template_id
          : parseInt(newEquipment.value.equipment_template_id);

        // Find building and level IDs for this space
        let buildingId = null;
        let buildingLevelId = null;

        if (localHierarchyData.value?.buildings) {
          for (const building of localHierarchyData.value.buildings) {
            if (building.levels) {
              for (const level of building.levels) {
                if (level.spaces) {
                  const foundSpace = level.spaces.find((s) => s.id === newEquipment.value.space_id);
                  if (foundSpace) {
                    buildingId = building.id;
                    buildingLevelId = level.id;
                    break;
                  }
                }
              }
              if (buildingId) {break;}
            }
          }
        }

        if (!buildingId) {
          throw new Error('Could not determine building for the selected space');
        }

        // Build request body for template instantiation
        const requestBody = {
          template_id: templateId,
          name: newEquipment.value.name,
          space_id: newEquipment.value.space_id,
          building_id: buildingId,
        };

        if (buildingLevelId) {
          requestBody.building_level_id = buildingLevelId;
        }

        if (newEquipment.value.description) {
          requestBody.description = newEquipment.value.description;
        }

        // If in quote context, use quote equipment API
        if (props.quoteContext?.quote_id) {
          result = await quoteService.createQuoteEquipment(props.quoteContext.quote_id, requestBody);
          result = result.data || result;
        } else {
          // Call the template instantiation endpoint (special case - not in composable)
          const response = await authenticatedFetch(`/api/equipment-templates/${templateId}/instantiate`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Failed to create equipment from template (HTTP ${response.status})`);
          }

          result = await response.json();
          result = result.data || result;
        }
      } else {
        // Adding equipment to space (manual creation without template)
        const equipmentData = {
          name: newEquipment.value.name,
          description: newEquipment.value.description || null,
          space_id: newEquipment.value.space_id,
          brick_class: newEquipment.value.brick_class,
        };

        // If in quote context, use quote equipment API
        if (props.quoteContext?.quote_id) {
          // Find building and level IDs for this space (needed for quote API)
          let buildingId = null;
          let buildingLevelId = null;

          if (localHierarchyData.value?.buildings) {
            for (const building of localHierarchyData.value.buildings) {
              if (building.levels) {
                for (const level of building.levels) {
                  if (level.spaces) {
                    const foundSpace = level.spaces.find((s) => s.id === equipmentData.space_id);
                    if (foundSpace) {
                      buildingId = building.id;
                      buildingLevelId = level.id;
                      break;
                    }
                  }
                }
                if (buildingId) {break;}
              }
            }
          }

          const requestBody = {
            ...equipmentData,
            building_id: buildingId,
          };

          if (buildingLevelId) {
            requestBody.building_level_id = buildingLevelId;
          }

          result = await quoteService.createQuoteEquipment(props.quoteContext.quote_id, requestBody);
          result = result.data || result;

          $q.notify({
            type: 'positive',
            message: `Equipment "${equipmentData.name}" created in quote context`,
          });
        } else {
          // Use composable for simple equipment creation
          result = await equipmentApi.createEquipment(equipmentData);
        }
      }

      // Handle feeds relationship for VAVs/Terminal Units (same as zones)
      if (isVAVOrTerminalUnit(newEquipment.value) && newEquipment.value.serves_space) {
        const newEquipmentId = result.id;
        if (newEquipmentId) {
          await authenticatedFetch('/api/relationships', {
            method: 'POST',
            body: JSON.stringify({
              source_type: 'SiteEquipment',
              source_id: newEquipmentId,
              target_type: 'Space',
              target_id: newEquipment.value.serves_space,
              relationship_type: 'feeds',
            }),
          });
        }
      }

      // Success notification is shown by composable
      showEquipmentDialog.value = false;

      // Refresh data to show the new equipment
      await refreshData();
    } catch (error) {
      console.error('Failed to save equipment:', error);
    } finally {
      saving.value = false;
    }
  };

  // NOTE: addPoint moved to EquipmentManager component

  // NOTE: Update functions for editing entities (updateBuilding, updateLevel, updateSpace) moved to HierarchyManager component

  const updateEquipment = async () => {
    if (!editingEquipment.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Equipment name is required',
      });
      return;
    }

    saving.value = true;
    try {
      // Prepare metadata with power classification
      const metadata = {};
      if (editingEquipment.value.power_classification) {
        metadata.power_classification = editingEquipment.value.power_classification;
      }

      // Prepare controller templates for DDC panels (many-to-many)
      const controller_templates = editingEquipment.value.ddcControllers?.map((ctrl) => ({
        id: ctrl.id,
        quantity: ctrl.pivot?.quantity || 1,
        position_label: ctrl.pivot?.position_label || null,
      })) || [];

      // Build update data
      const updateData = {
        name: editingEquipment.value.name,
        description: editingEquipment.value.description,
        brick_class: editingEquipment.value.brick_class,
      };

      if (Object.keys(metadata).length > 0) {
        updateData.metadata = metadata;
      }

      if (controller_templates.length > 0) {
        updateData.controller_templates = controller_templates;
      }

      // Use composable for basic equipment update
      await equipmentApi.updateEquipment(editingEquipment.value.id, updateData);

      // Handle DDC Controller allocation/deallocation
      if (editingEquipment.value.controller_id) {
        // Allocate controller
        await authenticatedFetch(
          `/api/v2/sites/${props.site.id}/equipment/${editingEquipment.value.id}/allocate-controller`,
          {
            method: 'POST',
            body: JSON.stringify({
              controller_id: editingEquipment.value.controller_id,
              cascade_to_devices: true,
            }),
          },
        );
      } else {
        // Deallocate controller (if had one before)
        try {
          await authenticatedFetch(
            `/api/v2/sites/${props.site.id}/equipment/${editingEquipment.value.id}/deallocate-controller`,
            {
              method: 'DELETE',
            },
          );
        } catch (error) {
          console.warn('Failed to deallocate controller (may not have been allocated):', error);
        }
      }

      // Handle Power Source connection/disconnection
      if (editingEquipment.value.power_source_id) {
        // Connect power source
        await authenticatedFetch(
          `/api/v2/sites/${props.site.id}/equipment/${editingEquipment.value.id}/connect-power`,
          {
            method: 'POST',
            body: JSON.stringify({
              source_id: editingEquipment.value.power_source_id,
            }),
          },
        );
      } else {
        // Disconnect power (if had one before)
        try {
          await authenticatedFetch(
            `/api/v2/sites/${props.site.id}/equipment/${editingEquipment.value.id}/disconnect-power`,
            {
              method: 'DELETE',
            },
          );
        } catch (error) {
          console.warn('Failed to disconnect power (may not have been connected):', error);
        }
      }

      // Handle feeds relationship for VAVs/Terminal Units (same as zones)
      if (isVAVOrTerminalUnit(editingEquipment.value)) {
        // Get existing feeds relationships
        const relResponse = await authenticatedFetch(`/api/v2/sites/${props.site.id}/equipment/${editingEquipment.value.id}/relationships`);
        if (relResponse.ok) {
          const relData = await relResponse.json();
          const feedsRelationships = relData.data?.filter(
            (rel) => rel.relationship_type === 'feeds' && rel.target_type === 'Space',
          ) || [];

          // Delete existing feeds relationships
          for (const rel of feedsRelationships) {
            await authenticatedFetch(`/api/relationships/${rel.id}`, {
              method: 'DELETE',
            });
          }
        }

        // Create new feeds relationship if space is selected
        if (editingEquipment.value.serves_space) {
          await authenticatedFetch('/api/relationships', {
            method: 'POST',
            body: JSON.stringify({
              source_type: 'SiteEquipment',
              source_id: editingEquipment.value.id,
              target_type: 'Space',
              target_id: editingEquipment.value.serves_space,
              relationship_type: 'feeds',
            }),
          });
        }
      }

      // Success notification already shown by composable
      showEditEquipmentDialog.value = false;
      await refreshData();
    } catch (error) {
      console.error('Failed to update equipment:', error);
    } finally {
      saving.value = false;
    }
  };

  const updateDevice = async () => {
    if (!editingDevice.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Device name is required',
      });
      return;
    }

    try {
      const deviceData = {
        name: editingDevice.value.name,
        brick_class: editingDevice.value.brick_class,
        serial_number: editingDevice.value.serial_number,
        device_part_id: editingDevice.value.device_part_id,
        properties: editingDevice.value.properties || {},
        location_space_id: editingDevice.value.location_space_id,
        location_description: editingDevice.value.location_description,
      };

      await deviceApi.updateDevice(editingDevice.value.id, deviceData);

      showEditDeviceDialog.value = false;

      // Force complete refresh with cache bypass
      await refreshData(true);
    } catch (error) {
      console.error('Failed to update device:', error);
    }
  };

  const updatePoint = async () => {
    if (!editingPoint.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Point name is required',
      });
      return;
    }

    try {
      const pointData = {
        name: editingPoint.value.name,
        brick_class: editingPoint.value.brick_class,
        point_type: editingPoint.value.point_type || 'Virtual',
        unit_mapping_id: editingPoint.value.unit_mapping_id || null,
      };

      await pointApi.updatePoint(editingPoint.value.id, pointData);

      showEditPointDialog.value = false;

      // Immediately refresh with cache bypass to show updated data
      await refreshData(true);
    } catch (error) {
      console.error('Failed to update point:', error);
    }
  };

  // Template form methods

  const onEquipmentTemplateSelect = (template) => {
    if (!template) {
      // Template was cleared, reset related fields
      newEquipment.value.selectedTemplate = null;
      newEquipment.value.equipment_template_id = null;
      newEquipment.value.brick_class = '';
      newEquipment.value.description = '';
      return;
    }

    // Store the selected template (template is already the full object from q-select)
    newEquipment.value.selectedTemplate = template;
    // Extract the numeric ID from the template object's value property
    newEquipment.value.equipment_template_id = typeof template.value === 'number' ? template.value : template.id;

    // Pre-populate fields from template
    if (template.brick_class) {
      newEquipment.value.brick_class = template.brick_class;
    }
    if (template.description) {
      newEquipment.value.description = template.description;
    }
  };

  // filterBrickClasses moved to useFormHelpers composable

  // Removed updatePointDefaults and updateBrickClassDefaults functions
  // No longer needed with direct BRICK class selection

  // updateLevelDefaults moved to useFormHelpers composable

  // updateSpaceDefaults moved to useFormHelpers composable

  // Note: confirmDelete function is defined earlier with comprehensive deletion logic


  const selectSwitchboard = (switchboard) => {
    selectedSwitchboard.value = switchboard;
  // Selected switchboard
  };

  const editSwitchboard = (switchboard) => {
  // Editing switchboard
  };

  const addEquipmentToSwitchboard = (switchboard) => {
    selectedSwitchboard.value = switchboard;
    showEquipmentAssignment.value = true;
  };

  // Compound Methods - Support for both equipment and device compounds
  // Note: isCompoundEquipment and getCompoundTooltip moved to useNodeTypeHelpers composable

  // Equipment compound assembly
  const startCompoundAssembly = async (node) => {
    $q.notify({
      type: 'warning',
      message: 'Equipment compound assembly is no longer supported. Use device compound assembly instead.',
    });
    return;

  /* NOTE: Code below is disabled but kept for reference
  // Start assembly process - collect equipment from same space
  compoundOps.selectedComponentsForAssembly.value = []

  // Get space ID from the selected equipment
  const spaceId = node.data?.space_id || node.spaceId
  if (!spaceId) {
    $q.notify({
      type: 'warning',
      message: 'Equipment must be in a space to assemble compounds'
    })
    return
  }

  // Find all equipment in the same space
  const spaceEquipment = await loadSpaceEquipment(spaceId)

  // Extract equipment ID properly - handle both numeric and string formats
  let equipmentId = node.data?.id || node.id
  if (typeof equipmentId === 'string' && equipmentId.includes('equipment-')) {
    equipmentId = parseInt(equipmentId.replace('equipment-', ''))
  } else if (typeof equipmentId === 'string') {
    equipmentId = parseInt(equipmentId)
  }


  // Pre-select the clicked equipment
  compoundOps.selectedComponentsForAssembly.value = [
    {
      id: equipmentId,
      name: node.label || node.data?.name,
      brick_class: node.data?.brick_class || node.brickClass,
      icon: node.icon,
      relationship: 'hasPart'
    }
  ]

  // Open multi-select dialog
  $q.dialog({
    title: 'Select Equipment to Assemble',
    message: 'Choose additional equipment from this space to include in the compound:',
    options: {
      type: 'checkbox',
      model: [],
      items: spaceEquipment
        .filter(eq => eq.id !== equipmentId && !eq.is_compound)
        .map(eq => ({
          label: `${eq.name} (${eq.brick_class})`,
          value: eq.id,
          data: eq
        }))
    },
    cancel: true,
    persistent: true
  }).onOk(selected => {
    if (selected && selected.length > 0) {
      // Add selected equipment to compound list
      selected.forEach(eqId => {
        const equipment = spaceEquipment.find(eq => eq.id === eqId)
        if (equipment) {
          compoundOps.selectedComponentsForAssembly.value.push({
            id: equipment.id,
            name: equipment.name,
            brick_class: equipment.brick_class,
            icon: 'precision_manufacturing',
            relationship: 'hasPart'
          })
        }
      })

      // Reset compound form
      compoundOps.compoundFormData.value = {
        name: '',
        description: '',
        brick_class: '',
        compound_type: 'manual',
        parent_role: 'controller',
        components: compoundOps.selectedComponentsForAssembly.value
      }

      // Show assembly dialog
      compoundOps.compoundAssemblyDialogVisible.value = true
    } else {
      $q.notify({
        type: 'warning',
        message: 'Please select at least one additional equipment to create a compound'
      })
    }
  })
  */
  };

  const loadSpaceEquipment = async (spaceId) => {
    try {
      const response = await authenticatedFetch(`/api/v2/sites/${props.site.id}/spaces/${spaceId}/equipment`);
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to load space equipment:', error);
      return [];
    }
  };

  // Wrapper for composable's closeCompoundAssemblyDialog
  // Note: This function is kept for compatibility but delegates to composable
  const cancelCompoundAssembly = () => {
    compoundOps.closeCompoundAssemblyDialog();
  };

  const submitCompoundAssembly = async () => {
    // Use composable's assembleCompound with an API callback
    const apiCallback = async (compoundData) => {
      // Get the primary item (first in the list)
      const primaryItem = compoundOps.selectedComponentsForAssembly.value[0];

      // Determine if this is a device or equipment compound
      const isDevice = compoundOps.compoundFormData.value.item_type === 'device';

      // Prepare the assembly request according to backend expectations
      const componentIds = compoundOps.selectedComponentsForAssembly.value.slice(1).map((c) => c.id);
      const primaryItemId = primaryItem.id;

      // Build relationships array for metadata
      const relationships = [];
      componentIds.forEach((componentId) => {
        relationships.push({
          parent_id: primaryItemId,
          child_id: componentId,
          type: 'hasPart',
        });
      });

      // Build component roles map
      const componentRoles = {};
      compoundOps.selectedComponentsForAssembly.value.slice(1).forEach((c) => {
        if (c.relationship || compoundOps.compoundFormData.value.parent_role) {
          componentRoles[c.id] = c.relationship || compoundOps.compoundFormData.value.parent_role || 'component';
        }
      });

      const assemblyData = {
        component_ids: componentIds,
        metadata: {
          description: compoundOps.compoundFormData.value.description || '',
          component_roles: componentRoles,
          relationships: relationships,
          notes: `Assembled from ${componentIds.length} components: ${compoundOps.compoundFormData.value.name}`,
        },
      };

      // Use the appropriate endpoint based on item type
      const endpoint = isDevice
        ? `/api/v2/sites/${props.site.id}/devices/${primaryItem.id}/assemble`
        : `/api/equipment/${primaryItem.id}/assemble`;

      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(assemblyData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `Failed to create compound ${isDevice ? 'device' : 'equipment'}`);
      }

      return result;
    };

    try {
      await compoundOps.assembleCompound(apiCallback);
      // Refresh data after successful assembly
      await refreshData(true);
    } catch (error) {
      console.error('Failed to assemble compound:', error);
    }
  };

  const showDisassembleConfirm = async (node) => {
    // Equipment compounds are no longer supported
    $q.notify({
      type: 'warning',
      message: 'Equipment compound disassembly is no longer supported. Use device compound functionality instead.',
    });
    return;
  };

  const confirmDisassemble = async () => {
    // Equipment compounds are no longer supported - this should not be called
    $q.notify({
      type: 'warning',
      message: 'Equipment compound disassembly is no longer supported. Use device compound functionality instead.',
    });
    return;
  };

  // Compound Device Methods (similar to equipment but for devices)
  // Note: isCompoundDevice and getCompoundDeviceTooltip moved to useNodeTypeHelpers composable

  const startDeviceCompoundAssembly = async (node) => {
    // Start assembly process - collect devices from same parent equipment
    compoundOps.selectedComponentsForAssembly.value = [];

    // Get parent equipment ID from the selected device
    // First try direct equipment_id, then try to find parent from tree structure
    let equipmentId = node.data?.equipment_id || node.equipmentId;

    // If no equipment_id, try to find the parent equipment/zone section from the tree
    if (!equipmentId) {
      // Find parent node in the tree - could be equipment or zoneSection
      const findParentEquipment = (nodes, targetId) => {
        for (const n of nodes) {
          // Check if this node has our device as a child
          if (n.children && n.children.some((child) => child.id === targetId)) {
            // Parent found - if it's a zone section or equipment, use its ID
            if (n.type === 'equipment' || n.type === 'zoneSection') {
              return n.data?.id;
            }
          }
          // Recursively search children
          if (n.children) {
            const found = findParentEquipment(n.children, targetId);
            if (found) {return found;}
          }
        }
        return null;
      };

      equipmentId = findParentEquipment(treeNodes.value, node.id);
    }

    if (!equipmentId) {
      $q.notify({
        type: 'warning',
        message: 'Device must be under equipment to assemble compounds',
      });
      return;
    }

    // Find all devices under the same equipment
    const equipmentDevices = await loadEquipmentDevices(equipmentId);

    // Extract device ID properly - handle both numeric and string formats (fixed 2025-09-22)
    let deviceId = node.data?.id || node.id;
    if (typeof deviceId === 'string' && deviceId.includes('device-')) {
      deviceId = parseInt(deviceId.replace('device-', ''));
    } else if (typeof deviceId === 'string') {
      deviceId = parseInt(deviceId);
    }


    // Pre-select the clicked device
    compoundOps.selectedComponentsForAssembly.value = [
      {
        id: deviceId,
        name: node.label || node.data?.name,
        brick_class: node.data?.brick_class || node.brickClass,
        icon: node.icon,
        relationship: 'hasPart',
      },
    ];

    // Open multi-select dialog for devices
    $q.dialog({
      title: 'Select Devices to Assemble',
      message: 'Choose additional devices from this equipment to include in the compound:',
      options: {
        type: 'checkbox',
        model: [],
        items: equipmentDevices
          .filter((dev) => dev.id !== deviceId && !dev.is_compound)
          .map((dev) => ({
            label: `${dev.name} (${dev.brick_class})`,
            value: dev.id,
            data: dev,
          })),
      },
      cancel: true,
    }).onOk((selectedItems) => {
      // Add selected devices to compound assembly
      selectedItems.forEach((itemId) => {
        const device = equipmentDevices.find((dev) => dev.id === itemId);
        if (device) {
          compoundOps.selectedComponentsForAssembly.value.push({
            id: device.id,
            name: device.name,
            brick_class: device.brick_class,
            icon: 'memory',
            relationship: 'hasPart',
          });
        }
      });

      // Set compound type to device
      compoundOps.compoundFormData.value = {
        name: '',
        description: '',
        brick_class: '',
        compound_type: 'manual',
        parent_role: 'controller',
        components: compoundOps.selectedComponentsForAssembly.value,
        item_type: 'device', // Specify this is a device compound
      };

      // Open compound configuration dialog
      compoundOps.compoundAssemblyDialogVisible.value = true;
    });
  };

  const loadEquipmentDevices = async (equipmentId) => {
    try {
      const response = await authenticatedFetch(`/api/v2/sites/${props.site.id}/devices?equipment_id=${equipmentId}`);
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to load equipment devices:', error);
      return [];
    }
  };

  const showDisassembleDeviceConfirm = async (node) => {
    compoundToDisassemble.value = node;

    // Extract device ID properly - handle both numeric and string formats
    let deviceId = node.data?.id || node.id;
    if (typeof deviceId === 'string' && deviceId.includes('device-')) {
      deviceId = parseInt(deviceId.replace('device-', ''));
    } else if (typeof deviceId === 'string') {
      deviceId = parseInt(deviceId);
    }


    // Use compound components from the node data (already loaded in hierarchy)
    try {
      // The hierarchy response now includes components for compound devices
      if (node.data && node.data.components) {
        compoundComponents.value = node.data.components || [];
        showDisassembleDialog.value = true;
      } else {
        // Fallback: try to find the device in the hierarchy data
        const device = localHierarchyData.value?.buildings
          ?.flatMap((b) => b.levels?.flatMap((l) => l.spaces?.flatMap((s) => s.equipment?.flatMap((e) => e.devices))))
          ?.find((d) => d?.id === deviceId);

        if (device && device.components) {
          compoundComponents.value = device.components || [];
          showDisassembleDialog.value = true;
        } else {
          throw new Error('Compound structure not found in hierarchy data');
        }
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load compound details: ${error.message}`,
      });
    }
  };

  const confirmDisassembleDevice = async () => {
    if (!compoundToDisassemble.value) {return;}

    savingDisassemble.value = true;

    // Extract device ID properly - handle both numeric and string formats
    let deviceId = compoundToDisassemble.value.data?.id || compoundToDisassemble.value.id;
    if (typeof deviceId === 'string' && deviceId.includes('device-')) {
      deviceId = parseInt(deviceId.replace('device-', ''));
    } else if (typeof deviceId === 'string') {
      deviceId = parseInt(deviceId);
    }


    try {
      const response = await authenticatedFetch(
        `/api/v2/sites/${props.site.id}/devices/${deviceId}/disassemble`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            preserve_relationships: true,
          }),
        },
      );

      const result = await response.json();

      if (response.ok) {
        $q.notify({
          type: 'positive',
          message: `Device disassembled into ${result.data?.component_count || 0} components`,
        });

        showDisassembleDialog.value = false;
        compoundToDisassemble.value = null;
        compoundComponents.value = [];

        // Refresh the tree
        await loadHierarchyData();
        buildTreeNodes();
      } else {
        throw new Error(result.message || 'Disassembly failed');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to disassemble: ${error.message}`,
      });
    } finally {
      savingDisassemble.value = false;
    }
  };

  const showCompoundTemplateDialogForSpace = async (node) => {
    // Always refresh compound templates when opening dialog to get latest templates
    await loadCompoundTemplates();

    // Store selected space
    selectedSpace.value = node.data || node;

    // Reset form using composable
    compoundOps.compoundFormData.value = {
      name: '',
      description: '',
      brick_class: '',
      compound_type: 'template',
      parent_role: 'controller',
      components: [],
    };

    selectedCompoundTemplate.value = null;
    showCompoundTemplateDialog.value = true;
  };

  const loadCompoundTemplates = async () => {
    try {
      // Use composable to load compound templates
      const rawTemplates = await templateApi.loadCompoundTemplates();

      // Transform templates to match expected format
      compoundTemplates.value = rawTemplates.map((template) => ({
        ...template,
        id: template.id,
        name: template.name,
        label: template.name,
        value: template.id,
        type: 'compound',
        brick_class: template.brick_class || 'brick:Equipment',
        components: template.components || template.devices || [],
        component_types: template.component_types || template.components?.map((c) => c.brick_class || c.type) || [],
        description: template.description || `Compound assembly with ${template.component_count || template.components?.length || 0} components`,
      }));
    } catch (error) {
      console.error('Failed to load compound templates:', error);
      compoundTemplates.value = [];
    }
  };

  // Note: getTemplateComponents moved to useNodeTypeHelpers composable
  // Wrapper version here that calls the composable with required data
  const getTemplateComponents = (templateId) => {
    return nodeTypeHelpers.getTemplateComponents(templateId, compoundTemplates.value);
  };

  // Wrapper for createFromTemplate that's called from the CompoundTemplateDialog
  const createFromTemplateWrapper = async () => {
    if (!selectedCompoundTemplate.value || !compoundOps.compoundFormData.value.name) {
      $q.notify({
        type: 'warning',
        message: 'Please select a template and enter a name',
      });
      return;
    }

    const template = compoundTemplates.value.find((t) => t.id === selectedCompoundTemplate.value);
    if (!template) {
      $q.notify({
        type: 'warning',
        message: 'Template not found',
      });
      return;
    }

    // Use composable's createFromTemplate with API callback
    const apiCallback = async (data) => {
      const response = await authenticatedFetch('/api/compound-devices/create-from-template', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          template_id: data.template_id,
          name: data.name,
          description: data.description,
          space_id: selectedSpace.value?.id,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create from template');
      }

      return result;
    };

    try {
      await compoundOps.createFromTemplate(template, apiCallback);

      // Close dialog and reset
      showCompoundTemplateDialog.value = false;
      selectedCompoundTemplate.value = null;

      // Refresh data
      await refreshData();
    } catch (error) {
      console.error('Failed to create from template:', error);
    }
  };

  // Zone Section Methods for AHUs
  // Note: isAHU, isZoneSection, isVAVOrTerminalUnit, isZone moved to useNodeTypeHelpers composable

  // Load existing feeds relationship for equipment
  const loadEquipmentFeedsRelationship = async (equipmentId) => {
    try {
      const response = await authenticatedFetch(`/api/v2/sites/${props.site.id}/equipment/${equipmentId}/relationships`);
      if (!response.ok) {return;}

      const data = await response.json();
      const feedsRelationships = data.data?.filter(
        (rel) => rel.relationship_type === 'feeds' && rel.target_type === 'Space',
      ) || [];

      if (feedsRelationships.length > 0) {
        // Set the first feeds relationship (should only be one)
        editingEquipment.value.serves_space = feedsRelationships[0].target_id;
      }
    } catch (error) {
      console.error('Failed to load equipment feeds relationship:', error);
    }
  };

  const showAddZoneSectionDialog = (node) => {
    // Adding zone section to AHU
    // Processing node data

    if (!isAHU(node)) {
      $q.notify({
        type: 'negative',
        message: 'Zone sections can only be added to Air Handling Units',
      });
      return;
    }

    // Check if the AHU is templated
    if (isTemplatedEquipment(node)) {
      $q.notify({
        type: 'warning',
        message: 'This AHU was created from a template and cannot be modified',
        caption: 'Zone sections for templated equipment are managed through the template',
        icon: 'warning',
        timeout: 5000,
      });
      return;
    }
  
    selectedAHU.value = node.data;
    newZoneSection.value = {
      name: '',
      zone_type: 'mixing',
      serves_space: null,
      brick_class: 'AHU_Zone_Section',
    };
    showZoneSectionDialog.value = true;
  };

  const resetZoneSectionDialog = () => {
    newZoneSection.value = {
      name: '',
      zone_type: 'mixing',
      serves_space: null,
      brick_class: 'AHU_Zone_Section',
    };
    selectedAHU.value = null;
    selectedZoneTemplate.value = null;
  };

  const applyZoneTemplate = (template) => {
    if (!template) {
      // Clear template values
      newZoneSection.value = {
        name: '',
        zone_type: 'mixing',
        serves_space: null,
        brick_class: 'AHU_Zone_Section',
      };
      return;
    }
  
    // Apply template values but allow overriding name
    newZoneSection.value = {
      ...newZoneSection.value,
      zone_type: template.zone_type || 'mixing',
      brick_class: 'AHU_Zone_Section',
      template_id: template.id,
    };
  
    // Suggest a name based on template
    if (!newZoneSection.value.name) {
      newZoneSection.value.name = `${template.name} - ${selectedAHU.value?.name || 'Zone'}`;
    }
  };

  // Wrapper function to call composable with all dependencies
  const addZoneSection = async () => {
    await hierarchyOps.addZoneSection(
      {
        name: newZoneSection.value.name,
        zone_type: newZoneSection.value.zone_type,
        template_id: newZoneSection.value.template_id,
        serves_space: newZoneSection.value.serves_space,
      },
      {
        $q,
        siteId: props.site.id,
        selectedAHU: selectedAHU.value,
        api,
        resetZoneSectionDialog,
        showZoneSectionDialog,
        refreshDataWithState,
      },
    );
  };

  // Zone Duplication and Template Functions
  const renameZoneSection = async (node) => {
    // Rename zone section dialog
    $q.dialog({
      title: 'Rename Zone Section',
      message: 'Enter a new name for this zone section:',
      prompt: {
        model: node.data?.name || node.label || '',
        type: 'text',
        isValid: (val) => val && val.trim().length > 0,
      },
      cancel: true,
      persistent: true,
    }).onOk(async (newName) => {
      try {
        // Update the zone section name via API
        const response = await authenticatedFetch(`/api/v2/sites/${props.site.id}/equipment/${node.data.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: newName.trim(),
            brick_class: node.data.brick_class || 'AHU_Zone_Section',
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to rename zone section');
        }

        const result = await response.json();

        $q.notify({
          type: 'positive',
          message: 'Zone section renamed successfully',
          icon: 'check',
        });

        // Reload the site hierarchy while preserving tree state
        await refreshDataWithState();
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to rename zone section: ' + error.message,
          icon: 'error',
        });
      }
    });
  };

  const duplicateZone = async (node) => {
    // First, prompt for the new zone name
    $q.dialog({
      title: 'Duplicate Zone',
      message: 'Enter a name for the duplicated zone:',
      prompt: {
        model: `${node.data.name} (Copy)`,
        type: 'text',
        isValid: (val) => val && val.length > 0,
      },
      cancel: true,
      persistent: true,
    }).onOk(async (newName) => {
      try {
        // The api service already unwraps the response, so we get the data directly
        // Use v2 duplicate endpoint for both zones and equipment (zones are equipment now)
        const endpoint = `/v2/equipment/${node.data.id}/duplicate`;
        const duplicatedItem = await api.post(endpoint, {
          equipment_id: node.data.id,
          name: newName, // Pass the new name to the API
        });
      
        // Since the API controller returns the duplicated item in the data property,
        // and the api service unwraps it, we receive the item object directly
        if (duplicatedItem) {
          $q.notify({
            type: 'positive',
            message: `Zone duplicated as "${newName}" successfully`,
          });

          // Preserve tree state and expand to show the new duplicated zone
          const state = preserveTreeState();
          await loadHierarchyData();

          // Find the parent node to expand (if zone is under an AHU)
          const nodesToExpand = [];
          if (node.data?.parent_equipment_id) {
            nodesToExpand.push(`equipment-${node.data.parent_equipment_id}`);
          }
          if (duplicatedItem.id) {
            nodesToExpand.push(`equipment-${duplicatedItem.id}`);
          }

          await restoreTreeState(state, nodesToExpand);
        }
      } catch (error) {
        // The error object from api service has message, status, and data properties
        $q.notify({
          type: 'negative',
          message: 'Failed to duplicate zone: ' + (error.message || 'Unknown error'),
        });
      }
    });
  };

  const createZoneTemplate = async (node) => {
    // Dialog to get template name
    $q.dialog({
      title: 'Create Zone Template',
      message: 'Enter a name for this zone template:',
      prompt: {
        model: '',
        type: 'text',
        isValid: (val) => val && val.length > 0,
      },
      cancel: true,
      persistent: true,
    }).onOk(async (templateName) => {
      try {
        // Use the proper save-as-template endpoint that captures devices
        const zoneId = node.data?.id || node.id;
        if (!zoneId) {
          throw new Error('Zone ID not found');
        }

        // Use the correct endpoint that captures all zone data including devices
        const response = await authenticatedFetch(`/api/v2/sites/${props.site.id}/equipment/${zoneId}/save-as-template`, {
          method: 'POST',
          body: JSON.stringify({
            name: templateName,
            description: `Template based on ${node.data?.name || node.label}`,
            include_devices: true,  // Include all devices in the template
            include_points: true,   // Include all points in the template
            scope: 'site',          // Site-wide template
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to save template');
        }

        const result = await response.json();

        if (result.success) {
          $q.notify({
            type: 'positive',
            message: 'Zone template created successfully with all devices',
          });
          // Reload templates if needed
          await loadEquipmentTemplates();
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to create template: ' + (error.message || 'Unknown error'),
        });
      }
    });
  };

  const deleteSwitchboard = (switchboard) => {
    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete switchboard "${switchboard.name}"?`,
      cancel: true,
      persistent: true,
    }).onOk(() => {
      $q.notify({
        type: 'positive',
        message: `Switchboard ${switchboard.name} deleted`,
      });
    });
  };

  const viewSwitchboardPoints = (switchboard) => {
    selectedSwitchboard.value = switchboard;
    showDDCPointsDialog.value = true;
  };

  const generateDDCPoints = () => {
    $q.notify({
      type: 'info',
      message: 'DDC point generation - Implementation in progress',
    });
  };

  const handleTreeNodeClick = (node) => {
    // Tree node clicked
    selectedNode.value = node.id;
  };

  // Watch tab changes to load data
  watch(tab, async (newTab) => {
    if (newTab === 'electrical') {
      if (!electricalData.value) {
        // Set loading state immediately before async operation
        loadingElectrical.value = true;
        await nextTick(); // Ensure spinner renders
        await loadElectricalHierarchy();
      } else {
        // Component handles its own rendering
        await nextTick();
      }
    } else if (newTab === 'airflow') {
      // Load AHU tree if not already loaded
      if (ahuTreeNodes.value.length === 0) {
        await loadAhuTree();
      }
    }
  });

  // Initialize component

  onMounted(async () => {
    // Load equipment templates
    loadEquipmentTemplates();

    // Load compound templates for device creation
    loadCompoundTemplates();

    // Load switchboard types from TTL
    loadSwitchboardTypes();

    // Force UI update to ensure tab panels are rendered with keep-alive
    await nextTick();

    // Small delay to ensure UI is fully rendered
    await new Promise((resolve) => setTimeout(resolve, 10));

    // Now load data - the spinners are already showing
    await Promise.all([
      loadHierarchyData(),
      loadEquipmentList(),
      loadSpaceAirflowData(),  // Load airflow connections from API
      loadDocumentationStatuses(),  // Load documentation status badges
    ]);
  });

// Helper function to infer quantity kind from BRICK class
// inferQuantityKind moved to useFormHelpers composable
</script>

<style scoped>
/* Fix for dialog pointer-events issue - force all dialog elements to be interactive */
:deep(.q-dialog) {
  pointer-events: auto !important;
}

:deep(.q-dialog__inner) {
  pointer-events: auto !important;
}

:deep(.q-dialog__inner > div) {
  pointer-events: auto !important;
}

:deep(.q-dialog__inner--minimized) {
  padding: 0;
}

.enhanced-schema-manager {
  height: 100vh;
  display: flex;
  flex-direction: column;
  pointer-events: auto !important;
}

/* Ensure spinner animation is always active when visible */
.q-spinner {
  animation: spin 1s linear infinite !important;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.schema-header {
  flex-shrink: 0;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

/* Location tree styles */
.location-tree {
  max-height: 400px;
  overflow-y: auto;
}

.location-tree :deep(.q-tree__node--selected) {
  background-color: rgba(25, 118, 210, 0.1);
}

.location-tree :deep(.q-tree__node-header) {
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.location-tree :deep(.q-tree__node-header:hover) {
  background-color: rgba(0, 0, 0, 0.04);
}

.q-card-section.q-pa-none {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.action-btn {
  min-width: 120px;
  font-weight: 600;
}

/* Enhanced styling following improved UI principles */
.q-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.q-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: box-shadow 0.3s ease;
}

.q-btn.action-btn {
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.q-btn.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Template preview card */
.template-preview-card {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
}

/* Visualization container styles */
.electrical-visualization-container,
.airflow-visualization-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.electrical-visualization-container .visualization-toolbar {
  border-bottom: 2px solid #e0e0e0;
  background: linear-gradient(to bottom, #ffffff, #f5f5f5);
}

.visualization-toolbar {
  border-bottom: 1px solid #e0e0e0;
}

.cytoscape-container {
  flex: 1;
  width: 100%;
  background: #fafafa;
  position: relative;
}

/* Enhanced electrical visualization styling */
.electrical-visualization-container .cytoscape-container {
  background: linear-gradient(to bottom, #ffffff, #f8f8f8);
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin: 10px;
}

/* Chart containers wrapper for split view */
.chart-containers-wrapper {
  flex: 1;
  display: flex;
  flex-direction: row;
  height: calc(100vh - 280px);
  min-height: 600px;
  position: relative;
}

/* Individual chart containers */
.chart-container {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.chart-container.full-view {
  width: 100%;
}

.chart-container.split-view-left {
  width: 50%;
  border-right: 2px solid #e0e0e0;
  flex-shrink: 0;
}

.chart-container.split-view-right {
  width: 50%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

/* Chart headers for split view */
.chart-header {
  padding: 8px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* ApexTree container */
.apex-tree-container {
  flex: 1;
  overflow: auto;
  padding: 20px;
  background: linear-gradient(to bottom, #f5f5f5, #ffffff);
  position: relative;
  display: block;
  width: 100%;
  min-height: 500px;
}

/* Custom tooltip styles for ApexTree */
.apex-tooltip-custom {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
}

.apex-tooltip-custom .tooltip-title {
  font-weight: bold;
  margin-bottom: 4px;
}

.apex-tooltip-custom .tooltip-content {
  font-size: 11px;
  opacity: 0.8;
}

/* Legend styling */
.electrical-visualization-container .q-chip {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Equipment table styling */
.equipment-management-table {
  border-radius: 8px;
  overflow: hidden;
}

.template-manager-table {
  border-radius: 8px;
  overflow: hidden;
}

/* Air flow container for multiple family trees */
.air-flow-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Device reference nodes - dual location feature */
/* These are visual indicators showing where devices are physically located */
.device-reference-node {
  color: #757575 !important;
  font-style: italic;
  opacity: 0.85;
  cursor: help;
}

.device-reference-node .q-icon {
  color: #1976d2 !important;
}

.device-reference-node:hover {
  opacity: 1;
  background-color: rgba(25, 118, 210, 0.05);
  border-radius: 4px;
}


</style>
